import type { Dictionary } from "./dictionary";

// Polish dictionary — the app's original/default locale. Component code
// should never contain a literal Polish string; use `useT()`/`getT()`
// instead (see lib/i18n/index.ts and lib/i18n/client.tsx).
//
// Not in scope here: seeded DB content (trainer bios, review text, certificate
// titles in lib/db/seed.ts) — that's data, not app copy, and would need a
// per-locale content strategy of its own. Also not in scope: DB enum values
// (activity.level, filter query-param values like "individual"/"group") —
// those are identifiers the code branches on, not text a user reads; only
// their *labels* belong here.
// Polish "few" plural form (2–4, 22–24, … but not 12–14): "2 zwroty" vs
// "5 zwrotów".
// "w listopadzie" — the locative Intl cannot produce.
const MONTHS_LOCATIVE = ["styczniu", "lutym", "marcu", "kwietniu", "maju", "czerwcu", "lipcu", "sierpniu", "wrześniu", "październiku", "listopadzie", "grudniu"];
const inMonth = (month: string) => `w ${MONTHS_LOCATIVE[Number(month.slice(5, 7)) - 1]}`;
const isPaucal = (n: number) => n % 10 >= 2 && n % 10 <= 4 && !(n % 100 >= 12 && n % 100 <= 14);

export const pl: Dictionary = {
  brand: "GoForCoach",

  validation: {
    required: "Wypełnij to pole.",
    typeMismatch: "Podaj prawidłową wartość.",
    tooShort: (min) => `Wpisz co najmniej ${min} znaków.`,
    tooLong: (max) => `Wpisz maksymalnie ${max} znaków.`,
    rangeUnderflow: (min) => `Wartość musi być co najmniej ${min}.`,
    rangeOverflow: (max) => `Wartość musi być maksymalnie ${max}.`,
    patternMismatch: "Wartość ma nieprawidłowy format.",
    badInput: "Podaj prawidłową wartość.",
  },

  nav: {
    home: "Strona główna",
    findTrainer: "Znajdź trenera",
    becomeTrainer: "Zostań trenerem",
    forTrainers: "Dla trenera",
    search: "Znajdź trenera",
    trainerCard: "Karta trenera",
    howItWorks: "Jak to działa",
    trainerPanel: "Panel trenera",
    clientPanel: "Moje treningi",
    openMenu: "Otwórz menu",
    closeMenu: "Zamknij menu",
    mainMenu: "Menu główne",
    sections: "Sekcje",
    sectionsTitle: "Przejdź do sekcji",
    language: "Język",
  },

  trainerPanel: {
    trainerTitle: "Panel trenera",
    clientTitle: "Panel klienta",
    trainerSub: "Twój pełny harmonogram zajęć.",
    clientSub: "Twoje nadchodzące rezerwacje.",
    noUpcomingTrainer: "Brak nadchodzących zajęć w harmonogramie.",
    sessionListEarlier: "Wcześniejsze zajęcia",
    sessionListLater: "Późniejsze zajęcia",
    noUpcomingClient: "Nie masz jeszcze zaplanowanego treningu.",
    withTrainer: "z",
    eventCapacity: (n: number) => `${n} os.`,
    groupCardName: (name: string) => `Grupa: ${name}`,
    showMoreTrainings: (hidden: number) => `Pokaż więcej (${hidden})`,
    showFewerTrainings: "Pokaż mniej",
    selectTraining: "Wybierz trening z listy, aby zobaczyć szczegóły.",
    backToCalendar: "Wróć do kalendarza",
    calendarOptions: "Opcje kalendarza",
    addTraining: "Dodaj trening",
    deselectDay: "Odznacz",
    openInMaps: "Otwórz na mapie",
    copyAddress: "Kopiuj adres",
    addressCopied: "Skopiowano adres",
    heroClients: "Klienci",
    heroTrainers: "Trenerzy",
    heroHours: "Godziny łącznie",
    heroNext: "Kolejny trening",
    needsAttentionTitle: "Wymaga uwagi",
    needsAttentionEmpty: "Brak nowych zgłoszeń — nowe rezerwacje i prośby klientów pojawią się tutaj, gdy tylko dotrą.",
    scheduleTitle: "Harmonogram",
    tabsLabel: "Nawigacja panelu",
    navStatus: "Status profilu",
    navCard: "Karta trenera",
    navOffer: "Oferta, godziny i miejsca",
    navCalendar: "Kalendarz",
    navStats: "Statystyki",
    cardView: "Zobacz profil publiczny",
    setupDone: "Konto aktywne",
    setupTitle: "Konfiguracja konta",
    contactTitle: "Dane kontaktowe",
    setupHeading: (remaining: number) =>
      `Konfiguracja konta · ${remaining === 1 ? "został 1 krok" : remaining <= 4 ? `zostały ${remaining} kroki` : `zostało ${remaining} kroków`}`,
    setupMissingLead: "Brakuje:",
    setupAwaitingEmail: "Potwierdź adres e-mail, aby założyć konto trenera.",
    clientsTitle: "Klienci, z którymi trenujesz",
    clientsEmpty: "Nie trenowałeś jeszcze z żadnym klientem.",
    statsTitle: "Statystyki zajęć",
    statsHours: "Godziny zrealizowane",
    statsSessions: "Zajęcia zrealizowane",
    statsTypes: "Rodzaje treningu",
    viewToggleLabel: "Widok kalendarza",
    viewMonth: "Miesiąc",
    viewWeek: "Tydzień",
    monthLegendSessions: "Zajęcia",
    monthLegendChangePending: "Zmiana czeka na klienta",
    monthLegendBlocked: "Czas zablokowany",
    prevMonth: "Poprzedni miesiąc",
    nextMonth: "Następny miesiąc",
    legend: {
      title: "Legenda",
      workingHoursAt: (place: string) => `Godziny pracy: ${place}`,
    },
  },

  timeBlock: {
    sectionTitle: "Zablokuj termin",
    intro: "Szybko oznacz siebie jako niedostępnego — np. wizyta u dentysty. Nowe rezerwacje nie będą możliwe w tym czasie; już umówione zajęcia zostają bez zmian.",
    addButton: "Zablokuj termin",
    dateLabel: "Data",
    startLabel: "Od",
    endLabel: "Do",
    durationLabel: "Ile czasu?",
    customDuration: "Inna godzina",
    durationPreset: (n: number) =>
      n < 60 ? `${n} min` : n % 60 === 0 ? `${n / 60} godz.` : `${Math.floor(n / 60)} godz. ${n % 60} min`,
    reasonLabel: "Powód (opcjonalnie)",
    reasonHint: "Widoczny tylko dla Ciebie.",
    reasonPlaceholder: "np. Dentysta",
    save: "Zablokuj",
    saving: "Zapisywanie...",
    cancel: "Anuluj",
    close: "Zamknij",
    listEmpty: "Brak zablokowanych terminów.",
    defaultReason: "Niedostępny",
    deleteLabel: "Usuń blokadę",
    deleteConfirmTitle: "Usunąć blokadę?",
    deleteConfirmMessage: "Ten czas znów będzie dostępny do rezerwacji.",
  },

  clientStats: {
    sectionTitle: "Twoje statystyki",
    monthTitle: "Treningi w tym miesiącu",
    monthBooked: "Zarezerwowane",
    monthCompleted: "Zrealizowane",
    monthPending: "Oczekujące",
    summaryTitle: "Podsumowanie",
    totalTrainings: "Treningi łącznie",
    totalHours: "Godziny treningowe",
    hoursValue: (n: number) => `${n.toLocaleString("pl-PL", { maximumFractionDigits: 1 })} h`,
    sessionsCountValue: (n: number) => {
      const mod10 = n % 10;
      const mod100 = n % 100;
      const word = n === 1 ? "zajęcie" : mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14) ? "zajęcia" : "zajęć";
      return `${n} ${word}`;
    },
    breakdownTitle: "Podział na rodzaj treningu",
    breakdownEmpty: "Brak zrealizowanych treningów.",
    columnType: "Rodzaj",
    columnCount: "Liczba",
    columnHours: "Godziny",
    trainersTitle: "Trenerzy, z którymi trenujesz",
    trainersEmpty: "Nie trenowałeś jeszcze z żadnym trenerem.",
    messageButton: "Napisz",
    conversationWith: (name: string) => `Rozmowa z ${name}`,
  },

  common: {
    noRatings: "Brak ocen",
    online: "Online",
    decimalSeparator: ",",
    reviewsCount: (n: number) => `${n} ${n === 1 ? "opinia" : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? "opinie" : "opinii"}`,
    book: "Umów",
    viewProfile: "Zobacz profil",
    individual: "Indywidualnie",
    group: (booked: number, capacity: number) => `Grupa ${booked}/${capacity}`,
    groupMax: (capacity: number) => `Grupa maks. ${capacity}`,
    nextSlot: (text: string) => `Najbliższy termin: ${text}`,
    removeFilter: "Usuń filtr",
    pln: "PLN",
    close: "Zamknij",
  },

  levels: {
    poczatkujacy: "Początkujący",
    sredni: "Średni",
    zaawansowany: "Zaawansowany",
    wszystkie: "Wszystkie poziomy",
  },

  amenities: {
    parking: "Parking",
    wifi: "Darmowe Wi-Fi",
    showers: "Prysznice",
    lockers: "Szatnia / szafki",
    pet_friendly: "Można przyjść z psem",
    kids_friendly: "Dzieci mile widziane",
    air_conditioning: "Klimatyzacja",
    equipment_included: "Sprzęt w cenie",
    wheelchair_accessible: "Dostęp dla niepełnosprawnych",
    water_dispenser: "Dostęp do wody",
  },

  time: {
    today: "dziś",
    tomorrow: "jutro",
    yesterday: "wczoraj",
    daysAgo: (n: number) => `${n} dni temu`,
    oneWeekAgo: "1 tydzień temu",
    weeksAgo: (n: number) => `${n} tygodnie temu`,
    oneMonthAgo: "1 miesiąc temu",
    monthsAgo: (n: number) => `${n} miesięcy temu`,
    weekdaysShort: ["ndz", "pon", "wt", "śr", "czw", "pt", "sob"],
    // Display order for full-week day pickers (Mon..Sun) — the day *values*
    // stay 0=Sun..6=Sat everywhere (matches the JS weekday numbering and FullCalendar),
    // this only controls what order checkboxes render in.
    weekDisplayOrder: [1, 2, 3, 4, 5, 6, 0],
    // Mon-first single/two-letter column headers for the mini day calendar.
    weekdayLettersMonFirst: ["Pn", "Wt", "Śr", "Cz", "Pt", "So", "Nd"],
    months: [
      "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
      "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
    ],
    monthsShort: ["sty", "lut", "mar", "kwi", "maj", "cze", "lip", "sie", "wrz", "paź", "lis", "gru"],
    day: "Dzień",
    month: "Miesiąc",
    year: "Rok",
    hour: "Godzina",
    minute: "Minuta",
  },

  search: {
    homeBarActivityLabel: "Czego szukasz?",
    homeBarWhereLabel: "Gdzie?",
    searchPlaceholder: "Np. pływanie, tenis, trening siłowy...",
    searchButton: "Szukaj trenerów",
    openSearch: "Otwórz wyszukiwanie",
    closeSearch: "Zamknij wyszukiwanie",
    suggestionsNoCity: "miejsce do ustalenia",
    resultsHeading: "Trenerzy",
    tocSearch: "Szukaj",
    resultCount: (n: number) => `${n} ${n === 1 ? "trener" : "trenerów"}`,
    noResults: "Brak trenerów spełniających wybrane kryteria.",
    noResultsHint: "Zmień lokalizację, sprawdź treningi online albo usuń filtry.",
    clearFilters: "Wyczyść filtry",
    loadingMore: "Wczytywanie kolejnych trenerów…",
    loadMore: "Pokaż więcej trenerów",
    fromPrice: (n: number) => `od ${n.toFixed(0)} zł`,
    fromPriceDuration: (n: number, min: number) => `od ${n.toFixed(0)} zł / ${min} min`,
    cityAndPlace: (cities: string[], places: string[]) => `${cities.join(", ")}${cities.length > 0 && places.length > 0 ? " • " : ""}${places.join(" · ")}`,
    matches: {
      show: (n: number) => `Pokaż dopasowania (${n})`,
      hide: "Skryj dopasowania",
      fieldLabel: {
        activityDescription: "Opis zajęć",
        bio: "O mnie",
        activityName: "Nazwa zajęć",
        discipline: "Dyscyplina",
        locationName: "Miejsce",
        city: "Miasto",
        street: "Ulica",
      },
    },
    filters: {
      level: "Poziom",
      format: "Forma zajęć",
      price: "Cena",
      rating: "Ocena",
      availability: "Dostępność",
      clearAll: "Wyczyść wszystko",
      audience: "Dla kogo",
      audienceAdults: "Dorośli",
      audienceChild: (age: number) =>
        `Dziecko: ${age} ${age === 1 ? "rok" : age % 10 >= 2 && age % 10 <= 4 && (age % 100 < 12 || age % 100 > 14) ? "lata" : "lat"}`,
      formatOptions: { individual: "Indywidualnie", group: "Grupa" },
      ratingOptions: { "4.5": "4.5 i wyżej", "4.0": "4.0 i wyżej" },
      availabilityOptions: { dzis: "Dziś", jutro: "Jutro", tydzien: "Ten tydzień" },
    },
    sort: {
      label: "Sortuj",
      rating: "Najlepiej oceniane",
      next: "Najbliższy termin",
      priceAsc: "Cena: rosnąco",
      priceDesc: "Cena: malejąco",
      distance: "Najbliżej",
    },
    locationPicker: {
      trigger: "Wybierz miejsce",
      triggerChosen: "Wybrane miejsce",
      geolocationDenied: "Nie udało się pobrać lokalizacji — wybierz miejsce ręcznie na mapie.",
      searchPlacePlaceholder: "Szukaj miasta lub adresu…",
      noPlaceMatches: "Brak wyników.",
      placeInputPlaceholder: "Miasto, ulica lub kod pocztowy",
      dragHint: "Kliknij na mapie lub przeciągnij pinezkę, aby dostosować miejsce.",
      expandMap: "Powiększ mapę",
      collapseMap: "Zmniejsz mapę",
      close: "Zamknij",
      radiusLabel: "Promień wyszukiwania",
      radiusThisCity: "To miasto",
      radiusUnlimited: "Bez limitu",
      radiusKm: (km: number) => `${km} km`,
      radiusTag: (km: number) => `Promień: ${km} km`,
      cityTag: (city: string) => `Miasto: ${city}`,
      confirm: "Zastosuj",
      clear: "Wyczyść lokalizację",
      distanceAway: (km: number) => `${km.toFixed(1)} km`,
      multiplePlacesFound: (query: string) => `Znaleziono kilka miejsc o nazwie „${query}” — wybierz:`,
      backToSearch: "← Wróć",
      districtOfCity: (district, city) => `${district} — dzielnica: ${city}`,
      cityInState: (city, state) => `${city} (${state})`,
      searching: "Szukam…",
    },
    noResultsInCity: (city: string) => `Brak trenerów w mieście ${city}.`,
    narrowedToCity: (city: string) => `Pokazujemy wyniki w mieście ${city}.`,
    widenTo50: "Poszerz zasięg do 50 km",
    widenToUnlimited: "Szukaj bez limitu",
  },

  trainerProfile: {
    accountVerified: "Konto potwierdzone",
    toc: {
      activities: "Zajęcia i kalendarz",
      gallery: "Galeria",
      certs: "Certyfikaty",
      bio: "O mnie",
      groups: "Dołącz do grupy treningowej",
      reviews: "Opinie",
      location: "Lokalizacja",
    },
    activitiesSub: "Wybierz zajęcia, potem termin",
    activitiesPlaceholder: "Tu pojawią się Twoje zajęcia. Dodasz je w sekcji Oferta, godziny i miejsca.",
    activitiesPlaceholderCta: "Przejdź do oferty",
    chooseTrainingLabel: "Wybierz trening",
    chooseTrainingOrLabel: "Albo wybierz trening",
    backToProfile: "Wróć do profilu trenera",
    step1Hint: "kliknij, by zobaczyć szczegóły",
    showDetails: "Zobacz szczegóły",
    hideDetails: "Ukryj szczegóły",
    calendar: "Kalendarz",
    busySlot: "Zajęte",
    workingHoursLabel: "Miejsca treningów i godziny pracy",
    workingHoursEmpty: "Trener nie podał godzin pracy.",
    noActivities: "Brak aktywnych zajęć w ofercie.",
    pickTrainingPrompt: "Wybierz trening, by zobaczyć dostępne terminy.",
    pickTrainingOrGroupPrompt: "Wybierz grupę lub trening, by zobaczyć szczegóły i terminy.",
    noPhotos: "Brak zdjęć w galerii.",
    openPhoto: "Zobacz zdjęcie",
    closePhoto: "Zamknij zdjęcie",
    previousPhoto: "Poprzednie zdjęcie",
    nextPhoto: "Następne zdjęcie",
    noCerts: "Brak certyfikatów.",
    noBio: "Trener nie dodał jeszcze opisu.",
    stats: {
      experienceValue: (years: number) => `${years} lat`,
      experience: "doświadczenia",
      sessionsCompleted: "zrealizowanych treningów",
      languages: "języki",
    },
    reviewTabs: {
      coach: (n: number) => `O trenerze (${n})`,
      activity: (n: number) => `O zajęciach (${n})`,
    },
    noReviews: "Brak opinii w tej kategorii.",
    locationUnset: "Lokalizacja nieustawiona",
    openInMaps: "Otwórz w mapach",
  },

  reviewForm: {
    sectionTitle: "Twoja opinia",
    rateCoach: "Oceń trenera",
    rateActivity: (name: string) => `Oceń: ${name}`,
    ratingLabel: "Ocena",
    textLabel: "Komentarz",
    textPlaceholder: "Jak minęły zajęcia?",
    submit: "Wyślij opinię",
    submitting: "Wysyłanie…",
    submitted: "Dziękujemy za opinię!",
    cancel: "Anuluj",
    notSignedIn: "Zaloguj się, aby zostawić opinię.",
    noCompletedSessions: "Opinię możesz dodać po odbytych zajęciach u tego trenera.",
    alreadyReviewed: "Ta opinia została już dodana.",
    notEligible: "Możesz ocenić tylko zajęcia, które faktycznie się odbyły.",
    invalidInput: "Nieprawidłowe dane.",
    emptyText: "Komentarz nie może być pusty.",
    textTooLong: "Komentarz jest za długi.",
    genericError: "Coś poszło nie tak. Spróbuj ponownie.",
  },

  booking: {
    showCalendar: "Umów",
    hideCalendar: "Ukryj kalendarz",
    trainerLabel: "Trener",
    placeLabel: "Miejsce spotkania",
    noSpecificPlace: "Miejsce do ustalenia z trenerem",
    openInGoogleMaps: "Otwórz w Google Maps",
    hoursLabel: "Godziny",
    pickSlotHint: "Wybierz termin na kalendarzu powyżej.",
    confirm: "Potwierdź",
    payAndBook: "Rezerwuję i płacę",
    summary: {
      duration: (min: number) => `Czas: ${min} min`,
      total: (price: string) => `Do zapłaty: ${price}`,
      cancellation:
        "Bezpłatne anulowanie najpóźniej 24 godziny przed treningiem. Później zwrot nie przysługuje, chyba że ustalisz z trenerem inny termin.",
      cancellationExample:
        "Przykład: trening w sobotę o 10:00 możesz anulować ze zwrotem do piątku do 10:00.",
      stripe: "Płatność obsługuje Stripe.",
      moreInfo: "Dodatkowe informacje",
      trainingWith: "Trening z:",
    },
    details: {
      title: "Szczegóły treningu",
      priceLabel: "Cena",
      clientLabel: "Klient",
      clientsLabel: "Klienci",
      infoButton: "Pokaż szczegóły treningu",
    },
    reserve: "Rezerwuj",
    review: {
      title: "Sprawdź rezerwację",
      subtitle: "Po kliknięciu „Rezerwuję i płacę” przejdziesz do płatności.",
      trainingLabel: "Trening",
      whenLabel: "Termin",
      placeLabel: "Miejsce",
      durationLabel: "Czas trwania",
      minutes: (n: number) => `${n} min`,
      attendeeLabel: "Dla",
      totalLabel: "Do zapłaty",
      cancellationTitle: "Zasady anulowania",
      back: "Wróć",
    },
    confirming: "Rezerwowanie…",
    cancel: "Anuluj",
    notOfferedThatDay: "Zajęcia nie są dostępne w tym dniu.",
    outsideWorkingHours: "Termin poza godzinami pracy.",
    holidayClosed: "W tym dniu trener nie przyjmuje rezerwacji (dzień wolny od pracy).",
    slotTaken: "Ten termin jest już zajęty.",
    notSignedIn: "Nie jesteś zalogowany.",
    loginToBook: "Zaloguj się, aby dokończyć rezerwację. Twój wybrany termin zostanie zachowany.",
    emailNotVerified: "Zweryfikuj adres e-mail, aby zarezerwować trening — link wysłaliśmy na Twoją skrzynkę.",
    invalidInput: "Nieprawidłowe dane.",
    invalidStartTime: "Nieprawidłowa godzina rozpoczęcia.",
    activityNotFound: "Nie znaleziono zajęć.",
    notBookable: "Te zajęcia nie są teraz dostępne do rezerwacji.",
    cannotBookOwn: "Nie możesz zarezerwować własnych zajęć.",
    mustBeFuture: "Termin musi być w przyszłości.",
    stepMismatch: (min: number) => `Godzina rozpoczęcia musi pasować do kroku ${min} min.`,
    slotNoLongerFree: "Ten termin nie jest już wolny.",
    noOpenSlots: "Brak wolnych terminów w najbliższych dniach.",
    previousWeek: "Poprzedni tydzień",
    nextWeek: "Następny tydzień",
    pickDayAndTime: "Wybierz dzień i godzinę",
    everyMinutes: (min: number) => `Co ${min} min`,
    closedDay: "Zamknięte",
    freeSlots: (n: number) =>
      `${n} ${n === 1 ? "wolny termin" : n >= 2 && n <= 4 ? "wolne terminy" : "wolnych terminów"}`,
    closedDayNote: "Niedostępne tego dnia — wybierz inny.",
    legend: {
      bookable: "Wolne",
      busy: "Zajęte",
      group: "Zajęcia grupowe",
    },
    change: "Zmień",
    closePicker: "Zamknij",
    fixedActivityNotAdHoc: "Te zajęcia mają stałe terminy — dołącz do jednego z terminów poniżej.",
    sessionNotFound: "Nie znaleziono terminu.",
    sessionAlreadyCancelled: "Ten trening został już odwołany.",
    groupNotFound: "Nie znaleziono grupy.",
    groupNotEmpty: "Można usunąć tylko grupę bez uczestników.",
    groupNotEnabled: "Stałe grupy nie są włączone dla tego konta.",
    groupNotDraft: "Ten okres został już zatwierdzony.",
    groupNoSessions: "Okres nie ma żadnych terminów.",
    groupNoFutureSessions: "W tym okresie nie ma już przyszłych treningów.",
    groupOverlap: "Któryś z terminów nakłada się na inny Twój trening.",
    groupPastDate: "Któryś z terminów jest w przeszłości.",
    groupOverCapacity: "Liczba uczestników przekracza limit miejsc w grupie.",
    groupPaymentProcessing: "Twoja płatność jest przetwarzana — odśwież stronę za chwilę.",
    groupChargeNotPayable: "Tej opłaty nie można już uregulować.",
    groupFull: "Brak wolnych miejsc w tej grupie.",
    groupSessionLocked: "Treningu stałej grupy nie można odwołać ani zarezerwować osobno. Skontaktuj się z obsługą.",
    absenceNotAllowed: "Tej nieobecności nie można zgłosić dla wybranego treningu.",
    sessionStarted: "Ten trening już się rozpoczął.",
    absenceAlreadyReported: "Nieobecność na ten trening została już zgłoszona.",
    absenceNotFound: "Nie znaleziono zgłoszenia nieobecności.",
    absenceNotOpen: "Tego zgłoszenia nie można już zmienić.",
    makeupInPast: "Termin odrabiania musi być w przyszłości.",
    makeupPastDeadline: "Termin odrabiania jest po ostatnim dozwolonym dniu (koniec następnego miesiąca).",
    makeupNotFound: "Nie znaleziono odrabianego treningu.",
    makeupNotScheduled: "Ten trening odrabiany nie jest już zaplanowany.",
    sessionNotCancellable: "Tego treningu nie można odwołać (już się rozpoczął, został odwołany lub nie należy do zatwierdzonego miesiąca).",
    refundNotFound: "Nie znaleziono zwrotu.",
    refundNotRetryable: "Ponowić można tylko nieudany zwrot.",
    alreadyJoined: "Jesteś już zapisany na ten termin.",
    sessionFull: "Brak wolnych miejsc w tym terminie.",
    activityNotForAdults: "Ten trening jest przeznaczony dla dzieci. Wybierz dziecko, aby go zarezerwować.",
    activityNotForChildren: "Ten trening nie jest przeznaczony dla dzieci.",
    childOutOfRange: (minAge: number, maxAge: number) =>
      `Wiek dziecka w dniu treningu nie mieści się w przedziale ${minAge}–${maxAge} lat.`,
    childNotFound: "Nie znaleziono dziecka.",
    join: "Dołącz",
    joining: "Zapisywanie…",
    joined: "Zapisano",
    full: "Brak miejsc",
    noUpcomingFixedSessions: "Brak nadchodzących terminów.",
    forbidden: "Nie masz dostępu do tej rezerwacji.",
    notFound: "Nie znaleziono rezerwacji.",
    notPending: "Ta rezerwacja nie oczekuje już na decyzję.",
    accept: "Akceptuj",
    accepting: "Akceptowanie…",
    decline: "Odrzuć",
    declining: "Odrzucanie…",
    cancelBooking: "Anuluj rezerwację",
    cancelling: "Anulowanie…",
    cancelConfirmTitle: "Anulować tę rezerwację?",
    cancelConfirmMessage: "Termin zostanie zwolniony, a tej operacji nie można cofnąć.",
    cancelConfirmYes: "Tak, anuluj",
    cancelConfirmNo: "Nie, zostaw",
    checkPayment: "Sprawdź płatność",
    checkingPayment: "Sprawdzanie…",
    paymentStillWaiting: "Nie widzimy jeszcze płatności. Jeśli już zapłacono, spróbuj ponownie za chwilę.",
    paymentCheckTooSoon: "Płatność została właśnie sprawdzona. Spróbuj ponownie za jakiś czas.",
    paymentCheckCancelled: "Płatność nie została dokończona w terminie, więc rezerwacja została anulowana.",
    alreadyPaidTitle: "Ten trening jest już opłacony",
    alreadyPaidMessage:
      "Płatność dotarła, więc rezerwacja nie została anulowana — jest teraz potwierdzona. Jeśli chcesz zrezygnować, anuluj ją ponownie, a zwrócimy pieniądze.",
    alreadyPaidOk: "Rozumiem",
    needsActionTitle: "Rezerwacje do zatwierdzenia",
    noNeedsAction: "Brak rezerwacji oczekujących na potwierdzenie.",
    newBookingsTitle: "Nowe rezerwacje",
    markSeen: "Oznacz jako obejrzane",
    markingSeen: "Oznaczanie…",
    notAcceptingPayments: "Ten trener nie ma jeszcze aktywnych płatności online.",
    paymentError: "Nie udało się rozpocząć płatności. Spróbuj ponownie.",
    serviceFeeLabel: "Opłata serwisowa",
    commissionLabel: "Prowizja",
    commissionInclStripe: (stripeFee) => `w tym opłata Stripe ${stripeFee} zł`,
    payoutLabel: "Twoja wypłata",
    paymentSuccessBanner: "Płatność przyjęta — Twoja rezerwacja jest potwierdzona.",
    paymentCancelledBanner: "Płatność anulowana — rezerwacja nie została opłacona.",
    status: {
      awaiting_payment: "Oczekuje na płatność",
      pending: "Oczekująca",
      confirmed: "Potwierdzona",
      cancelled: "Anulowana",
      completed: "Zakończona",
      no_show: "Nieobecność",
    },
  },

  auth: {
    emailVerified: {
      confirmedTitle: "Konto potwierdzone",
      confirmedBody: "Dziękujemy! Twój adres e-mail został potwierdzony, a Ty jesteś zalogowany.",
      alreadyTitle: "Konto już potwierdzone",
      alreadyBody: "Ten adres e-mail został już wcześniej potwierdzony.",
      expiredTitle: "Link wygasł",
      expiredBody: "Link potwierdzający stracił ważność. Spróbuj ponownie.",
      invalidTitle: "Nieprawidłowy link",
      invalidBody: "Ten link potwierdzający jest nieprawidłowy. Spróbuj ponownie.",
      close: "OK",
    },
    login: "Zaloguj",
    logout: "Wyloguj",
    emailLabel: "Email",
    emailPlaceholder: "twoj@email.pl",
    passwordLabel: "Hasło",
    confirmPasswordLabel: "Powtórz hasło",
    loginButton: "Zaloguj",
    loggingIn: "Logowanie…",
    registerButton: "Zarejestruj",
    registering: "Rejestracja…",
    backButton: "Wstecz",
    googleButton: "Zaloguj przez Google",
    or: "lub",
    noAccountLink: "Nie masz konta? Zarejestruj się",
    haveAccountLink: "Masz już konto? Zaloguj się",
    passwordMismatch: "Hasła nie są takie same.",
    passwordHint: "Co najmniej 9 znaków. Najlepiej kilka słów — może zawierać spacje.",
    passwordTooShortError: "Hasło musi mieć co najmniej 9 znaków.",
    passwordTooLongError: "Hasło może mieć maksymalnie 128 znaków.",
    passwordCompromisedError: "To hasło pojawiło się w wyciekach danych. Wybierz inne.",
    genericError: "Coś poszło nie tak. Spróbuj ponownie.",
    checkInboxTitle: "Sprawdź skrzynkę",
    checkInboxBody: (email: string) => `Wysłaliśmy link potwierdzający na ${email}.`,
    close: "Zamknij",
    forgotPasswordLink: "Nie pamiętasz hasła?",
    sendResetLink: "Wyślij link do resetu",
    sendingReset: "Wysyłanie…",
    resetSentTitle: "Sprawdź skrzynkę",
    resetSentBody: (email: string) => `Jeśli konto ${email} istnieje, wysłaliśmy na nie link do zresetowania hasła.`,
    captchaError: "Nie udało się potwierdzić, że nie jesteś robotem. Spróbuj ponownie.",
    googleOnlyAccountError:
      "To konto zostało założone przez Google — nie ustawiono jeszcze hasła. Zaloguj się przez Google albo kliknij „Nie pamiętasz hasła?”, aby je ustawić.",
  },

  resetPasswordPage: {
    title: "Ustaw nowe hasło",
    newPasswordLabel: "Nowe hasło",
    confirmPasswordLabel: "Powtórz nowe hasło",
    submitButton: "Zmień hasło",
    submitting: "Zapisywanie…",
    invalidTokenError: "Link do resetu hasła jest nieprawidłowy lub wygasł.",
    genericError: "Nie udało się zmienić hasła. Spróbuj ponownie.",
    successTitle: "Hasło zmienione",
    successBody: "Twoje hasło zostało zmienione. Możesz się teraz zalogować.",
    goHomeButton: "Przejdź do strony głównej",
  },

  trainerEdit: {
    save: "Zapisz",
    saving: "Zapisywanie…",
    cancel: "Anuluj",
    discardChanges: "Anuluj i odrzuć zmiany",
    delete: "Usuń",
    optionsMenu: "Opcje",
    confirmDeleteTitle: "Potwierdź usunięcie",
    addSection: (label: string) => `Dodaj sekcję: ${label}`,
    needsProfileFirst: "Najpierw zapisz podstawowe dane profilu po lewej.",
    genericError: "Coś poszło nie tak. Spróbuj ponownie.",
    preview: {
      title: "Podgląd profilu",
      subtitle: "Tak klienci widzą Twój opublikowany profil.",
      backToEdit: "Wróć do edycji",
      liveTitle: "Profil publiczny",
      liveSubtitle: "Tak klienci widzą Twój profil teraz.",
      viewBarPreview: "Podgląd profilu",
      viewBarLive: "Profil publiczny",
      notLiveAlert: "Profil nie jest jeszcze publiczny, więc nie możesz go zobaczyć. Możesz tylko obejrzeć podgląd.",
      checkStatus: "Sprawdź Status profilu",
    },
    contact: {
      emailLabel: "E-mail",
    },
    slug: {
      label: "Adres profilu",
      hint: "Domyślnie z imienia i nazwiska. Po zmianie stary link przestaje działać.",
      copy: "Kopiuj adres",
      copied: "Skopiowano",
    },
    onboarding: {
      title: (remaining: number) => {
        const word = remaining === 1 ? "krok" : remaining <= 4 ? "kroki" : "kroków";
        return `Jeszcze ${remaining} ${word} do widocznego profilu`;
      },
      subtitle: "Kolejność dowolna — możesz zrobić to teraz albo rozłożyć w czasie.",
      emailTodo: "Potwierdź adres e-mail",
      emailDone: "E-mail potwierdzony",
      profileTodo: "Uzupełnij profil",
      profileDone: "Profil uzupełniony",
      paymentsTodo: "Podłącz płatności",
      paymentsDone: "Płatności podłączone",
      termsTodo: "Zaakceptuj regulamin trenera",
      termsDone: "Regulamin trenera zaakceptowany",
      disabledTitle: "Konto zablokowane",
      disabledNoReason: "Skontaktuj się z nami, aby poznać powód.",
    },
    publish: {
      confirm: "Zatwierdź zmiany",
      publishing: "Publikowanie…",
      upToDate: "Wszystko opublikowane",
      missingAvatar: "zdjęcie profilowe",
      missingPhone: "numer telefonu",
      missingEmail: "adres e-mail",
      missingBio: "sekcję \"O mnie\"",
      missingActivity: "co najmniej jedną aktywność",
      changedSectionsLabel: "Niezapisane zmiany w:",
      discard: "Cofnij",
      discarding: "Cofanie…",
      leaveTitle: "Niepotwierdzone zmiany",
      leaveMessage:
        "Masz zmiany, które nie są jeszcze widoczne w profilu publicznym. Najpierw kliknij „Potwierdź zmiany”, w przeciwnym razie klienci ich nie zobaczą.",
      leaveConfirm: "Wyjdź mimo to",
      leaveStay: "Zostań na stronie",
      discardTitle: "Cofnąć zmiany?",
      discardMessage: (section: string) =>
        `Usuniesz niezapisane zmiany w sekcji „${section}” i wrócisz do aktualnie opublikowanej wersji. Tej operacji nie można cofnąć.`,
      sections: {
        avatar: "Zdjęcie profilowe",
        bio: "O mnie",
        infoCards: "Karty informacyjne",
        certificates: "Certyfikaty",
        gallery: "Galeria",
      },
    },
    core: {
      headlineLabel: "Nagłówek",
      headlinePlaceholder: "np. Trener personalny i przygotowanie motoryczne",
      photoLabel: "Zdjęcie profilowe",
      photoChange: "Zmień zdjęcie",
      photoAdd: "Dodaj zdjęcie",
      repositionLabel: "Wykadruj główne zdjęcie",
      cropModalTitle: "Dopasuj zdjęcie",
      cropZoomLabel: "Przybliżenie",
      cropSave: "Zapisz",
      cropCancel: "Anuluj",
      avatarTooLarge: "Zdjęcie jest za duże nawet po kompresji — wybierz mniejszy plik.",
      nameLabel: "Imię i nazwisko",
      phoneLabel: "Telefon",
      phoneEdit: "Edytuj numer telefonu",
      phoneConfirm: "Zatwierdź numer telefonu",
      phoneNotShared: "Nie jest widoczny dla klientów — służy tylko do kontaktu systemu z Tobą (np. powiadomienia).",
      confirmButton: "Zatwierdź",
    },
    connectPayments: {
      sectionLabel: "Płatności",
      intro:
        "Podłącz konto do otrzymywania płatności, aby klienci mogli płacić online za rezerwacje, a Ty otrzymywać wypłaty bezpośrednio na swoje konto.",
      statusNotConnected: "Nie podłączono",
      statusPending: "W trakcie weryfikacji",
      statusActive: "Aktywne",
      statusRestricted: "Wymaga uzupełnienia danych",
      connectButton: "Podłącz konto do otrzymywania płatności",
      continueSetupButton: "Dokończ konfigurację",
      connecting: "Łączenie…",
      dashboardButton: "Szczegóły konta do otrzymywania płatności",
      dashboardOpening: "Otwieranie…",
      genericError: "Coś poszło nie tak. Spróbuj ponownie.",
      commission: {
        title: "Prowizja platformy",
        rate: (percent: string) => `Twoja prowizja: ${percent}% ceny treningu. Obejmuje opłatę Stripe.`,
        ratePlusStripe: (percent: string) =>
          `Twoja prowizja: ${percent}% ceny treningu plus opłata Stripe (1 zł + 1,6% kwoty płatności).`,
        rateZero: "Twoja prowizja: 0%. Opłatę Stripe pokrywamy my.",
        sourceAdmin: "Stawkę ustawił administrator.",
        sourceAuto: (trainees: number, turnoverZl: string) =>
          `Stawka liczy się automatycznie 1. dnia miesiąca. Ocena za poprzedni miesiąc: ${trainees} podopiecznych, obrót ${turnoverZl} zł.`,
        sourceAutoPending: "Poziom prowizji liczy się automatycznie 1. dnia każdego miesiąca, na kolejny miesiąc, według liczby podopiecznych i obrotu.",
        modeLabel: "Jak rozliczyć prowizję?",
        modeDeducted: "Odejmij od ceny treningu",
        modeAdded: "Dolicz do ceny treningu",
        exampleDeducted: (client: string, trainer: string) =>
          `Przykład: trening za 100 zł. Klient płaci ${client} zł, Ty dostajesz ${trainer} zł.`,
        exampleAdded: (client: string, trainer: string) =>
          `Przykład: trening za 100 zł. Klient płaci ${client} zł, Ty dostajesz ${trainer} zł.`,
        appliesToNew: "Dotyczy nowych rezerwacji. Opłacone rezerwacje nie zmieniają się.",
        saved: "Zapisano",
      },
    },
    bio: {
      sectionLabel: "O mnie",
      bioLabel: "Opis",
      bioPlaceholder: "Opowiedz o swoim doświadczeniu i podejściu do treningów…",
      yearsExperienceLabel: "Lata doświadczenia",
      sessionsCompletedLabel: "Zrealizowane treningi",
      languagesLabel: "Języki",
      languagesPlaceholder: "np. PL / EN (B2)",
      equipmentNoteLabel: "Sprzęt / uwagi",
      equipmentNotePlaceholder: "np. Maty w cenie zajęć",
    },
    infoCards: {
      addCard: "Dodaj informację",
      pickPrompt: "Wybierz rodzaj informacji",
      presetExperience: "Lata doświadczenia",
      presetSessions: "Zrealizowane treningi",
      presetLanguages: "Języki",
      presetEquipment: "Sprzęt / uwagi",
      presetCustom: "Własna informacja",
      customLabelPlaceholder: "Nazwa (opcjonalnie)",
      customValuePlaceholder: "Treść",
    },
    certificates: {
      sectionLabel: "Certyfikaty",
      titleLabel: "Nazwa certyfikatu",
      metaLabel: "Informacja dodatkowa",
      metaPlaceholder: "np. Wydano 2022",
      add: "Dodaj certyfikat",
    },
    gallery: {
      sectionLabel: "Galeria",
      add: "Dodaj zdjęcie",
      uploading: "Wysyłanie…",
      editButton: "Edytuj galerię",
      dialogTitle: "Galeria",
      layoutLabel: "Układ",
      layouts: { hero: "Duże zdjęcie", equal: "Równe kafelki", mosaic: "Mozaika", strips: "Paski" },
      dragHandle: "Przeciągnij, aby zmienić kolejność",
      dragHint: "Przeciągnij zdjęcia, aby zmienić ich kolejność.",
      close: "Zamknij",
      empty: "Brak zdjęć.",
    },
    activities: {
      sectionLabel: "Zajęcia",
      add: "Dodaj zajęcia",
      edit: "Edytuj",
      confirmDelete: "Na pewno usunąć te zajęcia? Znikną z oferty i nie będzie można ich zarezerwować.",
      confirmDeleteTip: "Istniejące umówione zajęcia pozostaną bez zmian.",
      nameLabel: "Nazwa zajęć",
      descriptionLabel: "Opis zajęć (opcjonalnie)",
      descriptionPlaceholder: "Co obejmują te zajęcia, dla kogo są przeznaczone…",
      sportLabel: "Dyscyplina",
      sportSearchPlaceholder: "Szukaj dyscypliny…",
      sportNoMatches: "Brak wyników.",
      sportAddNew: (name: string) => `Dodaj nową dyscyplinę: „${name}”`,
      sportAdding: "Dodawanie…",
      sportPending: "oczekuje na potwierdzenie przez administratora",
      sportRequired: "Wybierz dyscyplinę z listy lub dodaj nową.",
      needsTrainerProfile: "Najpierw utwórz profil trenera.",
      locationLabel: "Godziny ustalania treningu i miejsce",
      onlineLabel: "Trening online",
      onlineHint: "Bez miejsca — klienci znajdą go niezależnie od miasta.",
      noSpecificLocation: "Bez konkretnego miejsca (ustalicie się z klientem)",
      addNewLocation: "+ Dodaj nowe miejsce treningu",
      noLocationShort: "miejsce do ustalenia",
      durationLabel: "Czas trwania (min)",
      priceLabel: "Cena (PLN)",
      capacityLabel: "Liczba miejsc (1 = indywidualnie)",
      levelLabel: "Poziom",
      audienceLabel: "Dla kogo",
      audienceAdults: "Dorośli",
      audienceChildren: "Dzieci",
      childMinAgeLabel: "Wiek od",
      childMaxAgeLabel: "Wiek do",
      audienceBadge: (allowsAdults: boolean, minAge: number | null, maxAge: number | null) => {
        const parts: string[] = [];
        if (allowsAdults) parts.push("Dorośli");
        if (minAge !== null && maxAge !== null) parts.push(`Dzieci ${minAge}–${maxAge} lat`);
        return parts.join(" + ");
      },
      stepMinutesLabel: "Co ile minut można rozpocząć rezerwację?",
      stepMinutesOption: (min: number) => (min < 60 ? `${min} min` : "1 godzina"),
      daysShort: ["Ndz", "Pon", "Wt", "Śr", "Czw", "Pt", "Sob"],
      empty: "Brak zajęć w ofercie.",
      gallery: (n: number) => `Galeria (${n})`,
      fixedToggleLabel: "Zarezerwuj z góry (stały termin w kalendarzu)",
      fixedToggleHint:
        "Zajęcia pojawią się w kalendarzu o wybranej godzinie, nawet jeśli nikt się jeszcze nie zapisał — np. stałe zajęcia grupowe.",
      fixedStartTimeLabel: "Godzina rozpoczęcia",
      fixedDaysLabel: "Dni tygodnia",
      fixedDaysRequired: "Wybierz co najmniej jeden dzień.",
      fixedBadge: "Zarezerwowane z góry:",
      pause: {
        menuLabel: "Więcej opcji",
        button: "Zawieś wszystkie zajęcia",
        intro: "Wszystkie Twoje zajęcia znikną z wyszukiwania i rezerwacji na wybrany czas.",
        modeLabel: "Na jak długo?",
        modeRange: "Wybierz zakres dat",
        modeIndefinite: "Bezterminowo (do odwołania)",
        startLabel: "Od",
        endLabel: "Do",
        confirm: "Zawieś zajęcia",
        activeRange: (start: string, end: string) => `Zajęcia zawieszone od ${start} do ${end}.`,
        activeIndefinite: "Zajęcia zawieszone bezterminowo.",
        resume: "Wznów zajęcia",
      },
      validation: {
        locationRequired: "Wybierz miejsce treningu.",
        nameMinLength: "Nazwa musi mieć min. 2 znaki.",
        durationPositive: "Czas trwania musi być większy od 0.",
        pricePositive: "Cena musi być większa od 0.",
        capacityMin: "Liczba miejsc musi wynosić co najmniej 1.",
        invalidTime: "Nieprawidłowa godzina.",
        stepInvalid: "Wybierz krok czasowy z listy.",
        audienceRequired: "Wybierz, dla kogo jest trening: dorośli, dzieci lub oboje.",
        childRangeIncomplete: "Podaj wiek dzieci od i do.",
        childRangeOrder: "Wiek „od” nie może być większy niż „do”.",
        childAgeBounds: "Wiek dzieci musi mieścić się w przedziale 1–17 lat.",
      },
    },
    workingHours: {
      sectionLabel: "Miejsca treningów i godziny pracy",
      add: "Dodaj miejsce treningu",
      placeNameLabel: "Nazwa miejsca",
      placeNamePlaceholder: "np. Crux Climbing Gym",
      cityLabel: "Miasto",
      requiredHint: "Pola wymagane: nazwa miejsca i miasto. Pozostałe dane miejsca są opcjonalne.",
      districtLabel: "Dzielnica",
      districtPlaceholder: "np. Mokotów",
      zipCodeLabel: "Kod pocztowy",
      zipCodePlaceholder: "00-000",
      streetLabel: "Ulica",
      buildingNumberLabel: "Nr budynku",
      flatNumberLabel: "Nr lokalu",
      additionalInfoLabel: "Dodatkowe informacje (np. wejście, piętro)",
      additionalInfoPlaceholder: "np. Wejście od podwórza, II piętro, domofon 12",
      addressPicker: {
        dragHint: "Kliknij na mapie lub przeciągnij pinezkę, aby wybrać adres.",
        selectedPrefix: "Wybrano",
        pinOptional: "PIN na mapie jest opcjonalny, ale ułatwia klientom dotarcie na miejsce.",
        changedHint:
          "Mapa zmieniła zaznaczone pola. Sprawdź adres i pinezkę na mapie oraz potwierdź, że wskazują to samo miejsce.",
        confirmAddress: "Potwierdź adres",
        districtOfCity: (district, city) => `${district} — dzielnica: ${city}`,
        cityInState: (city, state) => `${city} (${state})`,
      },
      fromLabel: "Od",
      toLabel: "Do",
      daysLabel: "Dni tygodnia",
      empty: "Brak zdefiniowanych godzin pracy.",
      addSubsection: "Dodaj godziny pracy w tym miejscu",
      editPlace: "Edytuj miejsce",
      confirmDelete: "Na pewno usunąć te godziny pracy?",
      gallery: (n: number) => `Zdjęcia miejsca treningu (${n})`,
      amenities: "Udogodnienia",
      amenitiesSaved: "Zapisano",
      slotsCount: (n: number) => `${n} ${n === 1 ? "termin" : n >= 2 && n <= 4 ? "terminy" : "terminów"}`,
      placesLabel: "Miejsca treningów",
      deletePlace: "Usuń miejsce",
      confirmDeletePlace: "Na pewno usunąć to miejsce z listy?",
      noHoursYet: "Brak godzin pracy w tym miejscu.",
      hoursLabel: "Godziny pracy",
      diagram: {
        title: "Tygodniowy plan pracy",
        overlap: "Paski: godziny pracy nakładają się w kilku miejscach",
        draft: "Niezapisane zmiany",
        newPlace: "Nowe miejsce",
        empty: "Wybierz dni i godziny, a zobaczysz je tutaj.",
      },
    },
  },

  home: {
    toc: {
      disciplines: "Dyscypliny",
      steps: "Jak to działa",
      movement: "Ruch",
      goodTrainer: "Dobry trener",
      recommended: "Polecani trenerzy",
      trainerCta: "Dla trenerów",
    },
    trainerCta: {
      title: "Jesteś trenerem? Daj się znaleźć.",
      text: "Pokaż swoją ofertę osobom, które szukają trenera w Twojej dyscyplinie. Załóż profil, dodaj dostępne terminy i pozwól nowym klientom Cię znaleźć.",
      cta: "Załóż konto trenera",
      imageAlt: "Trener w czapce",
    },
    movement: {
      title: "Ruch, do którego chce się wracać",
      text: "Sport może być celem, wyzwaniem, sposobem na lepszą formę albo po prostu chwilą dla siebie. Dobry trener pomaga znaleźć właściwy rytm i czerpać z treningu więcej przyjemności.",
      features: [
        { title: "Nie jesteś sam", text: "Masz obok siebie kogoś, kto wspiera Cię w każdej fazie drogi." },
        { title: "Widzisz swoje postępy", text: "Trening jest dopasowany do Twojego celu i możliwości." },
        { title: "Czerpiesz radość z ruchu", text: "Łatwiej utrzymać motywację, kiedy trening daje satysfakcję." },
      ],
      imageAlt: "Uśmiechnięta biegaczka nad rzeką",
    },
    goodTrainer: {
      title: "Dobry trener jest obok",
      text: "Podpowiada, poprawia, motywuje i pomaga znaleźć najlepszą drogę do Twojego celu. Dopasowuje trening do Ciebie — Twojego poziomu, możliwości i tempa.",
      points: [
        "Trening dopasowany do Twoich celów",
        "Wsparcie i motywacja",
        "Pomoc w rozwijaniu techniki",
        "Plan dopasowany do Twojego tempa",
      ],
      cta: "Znajdź swojego trenera",
      imageAlt: "Trener pomaga podopiecznej przy ćwiczeniu z kettlebell",
    },
    steps: {
      title: "Znajdź. Zarezerwuj. Trenuj.",
      items: [
        { title: "Znajdź trenera", text: "Wybierz dyscyplinę, lokalizację i osobę, która pasuje do Twoich potrzeb." },
        { title: "Wybierz termin", text: "Sprawdź dostępność i zarezerwuj trening." },
        { title: "Trenuj po swojemu", text: "Korzystaj ze wsparcia trenera, rozwijaj się i ciesz się ruchem." },
      ],
    },
    hero: {
      badge: "Platforma treningowa dla ludzi w ruchu",
      titleLine1: "Znajdź trenera.",
      titleLine2: "Ciesz się ruchem.",
      subtitle:
        "Dobierz trenera do swojego celu, poziomu i stylu treningu. Znajdź osobę, z którą dobrze Ci się trenuje i która pomoże Ci robić postępy w Twoim tempie.",
      cta: "Znajdź trenera",
      secondaryCta: "Jak to działa",
      trust: {
        verified: "Sprawdzeni trenerzy",
        payments: "Bezpieczne płatności",
        flexible: "Elastyczne terminy",
      },
      showcase: {
        trainingCaption: "Trening",
        trainingSub: "energia i regularność",
        goalCaption: "Cel",
        goalSub: "plan i konsekwencja",
        trainerCardLabel: "Karta trenera",
        trainerName: "Michał Nowak",
        trainerSpecialty: "Trening personalny · Kraków",
        trainerRating: "4,9",
        trainerReviews: "128 opinii",
        trainerTag1: "Redukcja",
        trainerTag2: "Siła",
        studentCardLabel: "Karta ucznia",
        studentGreeting: "Cześć, Ania!",
        studentGoal: "Cel: poprawić kondycję",
        studentProgressLabel: "do celu",
      },
    },
    searchCard: {
      sportLabel: "Jakiego sportu szukasz?",
      sportPlaceholder: "Wybierz sport",
      cityLabel: "Gdzie trenujesz?",
      cityPlaceholder: "Wpisz miasto",
    },
    recommended: {
      title: "Znajdź swojego trenera",
      viewAll: "Zobacz wszystkich",
      empty: "Brak polecanych trenerów.",
    },
    calendar: {
      title: "Twój kalendarz",
      empty: "Brak nadchodzących rezerwacji.",
      withTrainer: "z",
      allBookings: "Wszystkie rezerwacje",
    },
  },

  disciplines: {
    title: "Co chcesz trenować?",
    listLabel: "Dyscypliny",
  },

  howItWorks: {
    pill: "Prosty proces",
    titlePrefix: "Jak działa",
    titleEmphasis: "GoForCoach?",
    lead: "Od określenia celu do pierwszego treningu. Prosto, przejrzyście i bez chaosu w wiadomościach.",
    steps: [
      { title: "Określ cel", text: "Wybierz, czy chcesz poprawić kondycję, schudnąć, zbudować siłę lub wrócić do aktywności." },
      { title: "Porównaj trenerów", text: "Sprawdź specjalizacje, opinie, cennik, lokalizację i dostępne terminy." },
      { title: "Zarezerwuj termin", text: "Wybierz sesję pojedynczą lub pakiet i zarezerwuj trening w kilka kliknięć." },
      { title: "Śledź postępy", text: "Monitoruj aktywność, serię treningową, cele i osiągnięcia w panelu ucznia." },
    ],
    highlights: [
      { title: "Dopasowanie", text: "Filtry według sportu, celu, miasta, ceny i poziomu." },
      { title: "Zaufanie", text: "Profile trenerów zawierają doświadczenie, opinie i potwierdzone informacje." },
      { title: "Motywacja", text: "Panel ucznia wspiera regularność, postępy i dobre nawyki." },
    ],
    cta: {
      label: "Gotowa?",
      title: "Wybierz trenera i zacznij działać.",
      text: "Pierwszy trening może być początkiem nowego rytmu.",
      button: "Znajdź trenera",
    },
  },

  forCoach: {
    toc: {
      benefits: "Korzyści",
      profile: "Twój profil",
      steps: "Jak to działa",
      pricing: "Cennik",
      mission: "Misja",
      faq: "Pytania",
      join: "Dołącz",
    },
    metaTitle: "Dla trenerów — GoForCoach",
    eyebrow: "GOFORCOACH DLA TRENERÓW",
    titleLine: "Trenujesz innych?",
    titleAccent: "Daj się znaleźć.",
    lead: "GoForCoach pomaga osobom szukającym trenera trafić właśnie do Ciebie. Ty pokazujesz swoją ofertę, a rezerwacje i rozliczenia prowadzisz w jednym miejscu.",
    support: { strong: "Skup się na treningu.", rest: " My ułatwiamy drogę do rezerwacji." },
    cta: "Załóż konto trenera",
    micro: { strong: "Konto i profil: 0 zł.", rest: " Płacisz tylko od zrealizowanych treningów." },
    registerIntro: "Aby założyć profil trenera, najpierw utwórz konto — zajmie to chwilę.",
    heroAlt: "Trener podczas treningu z osobą ćwiczącą",
    benefits: [
      {
        title: "Bądź tam, gdzie szukają trenera",
        text: "Twoja oferta trafia do osób w Twojej okolicy, które aktywnie szukają treningów.",
      },
      {
        title: "Mniej ustalania. Więcej treningu.",
        text: "Osoba zainteresowana widzi dostępne terminy, wybiera ten, który jej odpowiada, i dokonuje rezerwacji.",
      },
      {
        title: "Pokaż, jak trenujesz",
        text: "Profil, oferta i doświadczenie pomagają szybko zobaczyć, czy jesteś właściwym trenerem.",
      },
    ],
    profile: {
      eyebrow: "TWÓJ PROFIL",
      title: "Twój profil pracuje na Twoją markę.",
      text: "Pokaż swoje specjalizacje, doświadczenie, opinie, lokalizacje i dostępne terminy. Stwórz profil, który przyciąga osoby szukające właśnie takiego trenera jak Ty.",
      devicesLabel: "Przykładowy profil trenera na komputerze i telefonie",
    },
    link: {
      kicker: "TWÓJ LINK TRENERSKI",
      titleLine: "Masz już osoby, które z Tobą trenują?",
      titleAccent: "Udostępnij im swój link trenerski.",
      text: "Każdy trener może wysłać bezpośredni link do swojego profilu. Osoba trenująca wybiera trening i termin, a rezerwacja trafia do Twojego kalendarza.",
      loginNote: { strong: "Po zalogowaniu", rest: " swój indywidualny link znajdziesz na profilu trenera." },
    },
    steps: {
      eyebrow: "JAK TO DZIAŁA",
      title: "4 kroki i gotowe.",
      items: [
        { title: "Załóż konto", text: "Kilka minut i możesz zacząć tworzyć swój profil." },
        { title: "Pokaż swoją ofertę", text: "Dodaj specjalizacje, miejsca treningów, ceny i dostępne terminy." },
        { title: "Połącz konto Stripe", text: "Bezpłatne konto Stripe obsługuje płatności i wypłaty." },
        {
          title: "Przyjmuj rezerwacje",
          text: "Osoby zainteresowane treningiem wybierają termin, a rezerwacja trafia do Twojego kalendarza.",
        },
      ],
    },
    pricing: {
      eyebrow: "PROSTE ZASADY",
      title: "Proste i uczciwe zasady.",
      cards: [
        { big: "0 zł", label: "konto i profil", text: "Założenie konta i prowadzenie profilu trenera jest bezpłatne." },
        {
          big: "5%",
          label: "od zrealizowanego treningu",
          text: "Płacisz tylko od zrealizowanych treningów. Im więcej ich odbędziesz w miesiącu, tym niższa prowizja.",
        },
        {
          big: "Ty ustalasz",
          label: "ceny i dostępność",
          text: "Sam decydujesz, ile kosztują Twoje treningi, gdzie się odbywają i jakie masz terminy.",
        },
      ],
    },
    mission: {
      eyebrow: "NASZA MISJA",
      title: "Łączymy tych, którzy chcą trenować, z tymi, którzy potrafią ich poprowadzić.",
      text: "Rozwijamy GoForCoach razem z trenerami, którzy są z nami od początku.",
      imgAlt: "Osoba aktywna podczas treningu w plenerze",
    },
    faq: {
      eyebrow: "NAJCZĘŚCIEJ ZADAWANE PYTANIA",
      title: "Masz pytania? Mamy odpowiedzi.",
      intro: "Nie ma tu odpowiedzi na Twoje pytanie? Zajrzyj do pełnej Pomocy GoForCoach.",
      moreLink: "Zobacz pełną Pomoc",
    },
    final: {
      title: "Daj się znaleźć. Zacznij przyjmować rezerwacje.",
      text: "Pokaż swoją ofertę, udostępnij swój profil i ułatw osobom szukającym treningu pierwszy krok do spotkania z Tobą.",
      cta: "Załóż konto trenera",
      imgAlt: "Aktywna osoba w plenerze",
    },
  },

  chat: {
    title: "Czat",
    withTrainer: "z",
    placeholder: "Napisz wiadomość…",
    send: "Wyślij",
    empty: "Brak wiadomości. Napisz pierwszą.",
    notSignedIn: "Nie jesteś zalogowany.",
    notFound: "Nie znaleziono czatu.",
    forbidden: "Nie masz dostępu do tego czatu.",
    invalidInput: "Nieprawidłowe dane.",
    emptyMessage: "Wiadomość nie może być pusta.",
    messageTooLong: "Wiadomość jest za długa.",
    sendMessageButton: "Wyślij zapytanie",
    backToProfile: "Powrót do profilu",
    emojiPicker: "Wstaw emoji",
    filterGeneral: "Ogólne",
    durationMinutes: (n: number) => `${n} min`,
    loadingOlder: "Ładowanie wcześniejszych wiadomości…",
    unread: "Nowa wiadomość",
    unpaid: "Nieopłacone",
  },

  messagesInbox: {
    title: "Wiadomości",
    empty: "Brak konwersacji.",
  },

  reschedule: {
    options: "Opcje treningu",
    changeTime: "Zmień termin",
    dateLabel: "Data",
    startLabel: "Godzina rozpoczęcia",
    save: "Zapisz nowy termin",
    saving: "Zapisywanie…",
    cancel: "Anuluj",
    clientsLabel: "Klienci",
    noClients: "Nikt nie zapisał się jeszcze na ten termin.",
    affectedClients: (n: number) =>
      n === 1 ? "Klient dostanie powiadomienie o zmianie." : `${n} klientów dostanie powiadomienie o zmianie.`,
    seenBadge: "Widział(a) zmianę",
    notSeenBadge: "Jeszcze nie widział(a)",
    bannerTitle: (activity: string, trainerName: string) => `Trener ${trainerName} zmienił termin: ${activity}`,
    bannerBody: (oldText: string, newText: string) => `Było: ${oldText}. Jest: ${newText}.`,
    was: (oldText: string) => `Było: ${oldText}`,
    gotIt: "Rozumiem",
    acknowledging: "Zapisywanie…",
    messageTrainer: "Napisz do trenera",
    changedChip: "Termin zmieniony",
  },

  cancelSession: {
    menuItem: "Odwołaj trening",
    title: "Odwołać ten trening?",
    message: (n: number) =>
      n === 0
        ? "Trening zostanie usunięty z Twojego kalendarza."
        : n === 1
          ? "Klient zostanie powiadomiony, a zapłacona kwota zwrócona."
          : isPaucal(n)
            ? `${n} klienci zostaną powiadomieni, a zapłacone kwoty zwrócone.`
            : `${n} klientów zostanie powiadomionych, a zapłacone kwoty zwrócone.`,
    clientRefund: (name: string, amount: string) => `${name} — zwrot ${amount} PLN`,
    clientUnpaid: (name: string) => `${name} — nieopłacone, bez zwrotu`,
    whoPaysTitle: "Kto ponosi koszt zwrotu",
    whoPays: [
      "Klient dostaje z powrotem całą zapłaconą kwotę.",
      "Z Twojego konta Stripe zostanie pobrana Twoja część płatności (cena minus prowizja platformy).",
      "Platforma zwraca swoją prowizję. Jej jedyny koszt to opłata procesora płatności, której Stripe nie zwraca.",
    ],
    ifFails:
      "Jeśli zwrot się nie uda (np. brak środków na Twoim koncie Stripe), trening i tak zostanie odwołany, klient dostanie informację, a nasz zespół dokończy zwrot ręcznie.",
    confirm: "Odwołaj trening",
    back: "Wróć",
    cancelledTitle: "Trening odwołany",
    closeButton: "Zamknij",
    freesTimeHint:
      "Odwołany trening zwalnia ten termin w Twoim kalendarzu — będzie można go znów zarezerwować. Jeśli chcesz, żeby ten czas pozostał zablokowany, dodaj go w sekcji „Zablokuj termin”.",
    refundFailedNotice: (n: number) =>
      n === 1
        ? "Trening odwołany, ale 1 zwrot się nie powiódł. Nasz zespół dokończy go ręcznie."
        : `Trening odwołany, ale ${n} ${isPaucal(n) ? "zwroty się nie powiodły" : "zwrotów się nie powiodło"}. Nasz zespół dokończy je ręcznie.`,
  },

  notifications: {
    title: "Powiadomienia",
    empty: "Brak powiadomień.",
    markAllRead: "Oznacz wszystkie jako przeczytane",
    detail: {
      open: "Przejdź",
      close: "Zamknij",
      finished: "Trening odbył się",
      inProgress: "Trening trwa",
      startsIn: (minutes: number) => `Za ${minutes} min`,
      rescheduled: (from: string, to: string) => `Termin zmieniony z ${from} na ${to}`,
    },
    sections: {
      new: "Nowe",
      today: "Dzisiaj",
      earlier: "Wcześniej",
    },
    types: {
      new_message: (from: string) => `Nowa wiadomość od ${from}`,
      today_session: "Masz dziś trening",
      upcoming_session: "Trening za niecałe 2 godziny",
      booking_requires_action: (clientName: string) => `Nowa rezerwacja od ${clientName}`,
      booking_confirmed: (trainerName: string) => `Twoja rezerwacja u trenera ${trainerName} jest potwierdzona`,
      booking_accepted: (trainerName: string) => `Trener ${trainerName} potwierdził Twoją rezerwację`,
      booking_declined: (trainerName: string) => `Trener ${trainerName} odrzucił Twoją rezerwację`,
      booking_cancelled: (clientName: string) => `${clientName} anulował(a) rezerwację`,
      session_rescheduled: (trainerName: string) => `Trener ${trainerName} zmienił termin Twojego treningu`,
      session_cancelled: (trainerName: string, refund: "none" | "refunded" | "failed") =>
        refund === "refunded"
          ? `Trener ${trainerName} odwołał trening. Zwrot płatności jest w drodze.`
          : refund === "failed"
            ? `Trener ${trainerName} odwołał trening. Zwrot płatności jest w trakcie realizacji.`
            : `Trener ${trainerName} odwołał trening.`,
      booking_expired: (trainerName: string) =>
        `Rezerwacja u trenera ${trainerName} wygasła, bo nie została opłacona na czas`,
      commission_changed: (oldPercent: string, newPercent: string) =>
        `Twoja prowizja zmieniła się z ${oldPercent}% na ${newPercent}%`,
      group_period_draft_ready: (groupName: string, month: string) =>
        `Grafik grupy „${groupName}” na ${month} jest gotowy do zatwierdzenia`,
      group_period_approved: (groupName: string, month: string) =>
        `Nowy miesiąc w grupie „${groupName}”: ${month} — zapłać w panelu`,
      group_payment_reminder: (groupName: string, month: string) =>
        `Zbliża się termin płatności za ${month} w grupie „${groupName}”`,
      group_payment_confirmed: (groupName: string, month: string) =>
        `Płatność za ${month} w grupie „${groupName}” przyjęta`,
      group_charge_expired: (groupName: string, month: string) =>
        `Nie opłacono ${month} — miejsce w grupie „${groupName}” zostało zwolnione`,
      group_absence_reported: (groupName: string, memberName: string) =>
        `${memberName} zgłosił(a) nieobecność w grupie „${groupName}” — wyznacz termin odrobienia`,
      group_makeup_assigned: (groupName: string) => `Trener wyznaczył termin odrobienia zajęć w grupie „${groupName}”`,
      group_makeup_cancelled: (groupName: string) =>
        `Trener odwołał termin odrabiania w grupie „${groupName}” — może wyznaczyć nowy termin`,
      group_session_cancelled: (groupName: string, refund?: string, unpaid?: "pending" | "voided") =>
        `Trener odwołał trening w grupie „${groupName}”` +
        (unpaid === "voided"
          ? " — nie musisz płacić za ten miesiąc"
          : unpaid === "pending"
            ? " — opłać miesiąc, a zwrócimy część za ten trening"
            : refund
              ? ` — zwrot ${refund} jest realizowany`
              : ""),
    },
    push: {
      new_message: (senderName: string) => ({
        title: "Nowa wiadomość",
        body: `${senderName} wysłał(a) Ci wiadomość`,
      }),
      booking_requires_action: (clientName: string) => ({
        title: "Nowa rezerwacja",
        body: `${clientName} zarezerwował(a) trening`,
      }),
      booking_confirmed: (trainerName: string) => ({
        title: "Rezerwacja potwierdzona",
        body: `Twój trening u trenera ${trainerName} jest potwierdzony`,
      }),
      booking_accepted: (trainerName: string) => ({
        title: "Rezerwacja potwierdzona",
        body: `Trener ${trainerName} potwierdził Twój trening`,
      }),
      booking_declined: (trainerName: string) => ({
        title: "Rezerwacja odrzucona",
        body: `Trener ${trainerName} nie może przyjąć tej rezerwacji`,
      }),
      booking_cancelled: (clientName: string) => ({
        title: "Rezerwacja anulowana",
        body: `${clientName} anulował(a) rezerwację`,
      }),
      session_rescheduled: (trainerName: string) => ({
        title: "Zmiana terminu treningu",
        body: `Trener ${trainerName} zmienił termin Twojego treningu`,
      }),
      session_cancelled: (trainerName: string) => ({
        title: "Trening odwołany",
        body: `Trener ${trainerName} odwołał Twój trening`,
      }),
      booking_expired: (trainerName: string) => ({
        title: "Rezerwacja wygasła",
        body: `Nie opłacono rezerwacji u trenera ${trainerName} na czas`,
      }),
    },
  },

  push: {
    notSignedIn: "Nie jesteś zalogowany.",
    trainerReason: "Otrzymuj powiadomienie na telefonie lub komputerze, gdy klient zarezerwuje termin.",
    clientReason: "Otrzymuj powiadomienia o potwierdzeniach i zmianach w Twoich rezerwacjach.",
    afterMessageReason: "Włącz powiadomienia push, aby nie przegapić odpowiedzi.",
    afterBookingReason: "Włącz powiadomienia push, aby nie przegapić informacji o rezerwacji.",
    enable: "Włącz powiadomienia",
    enabling: "Włączanie…",
    error: "Coś poszło nie tak. Sprawdź ustawienia przeglądarki i spróbuj ponownie.",
    settings: {
      enableReason: "Aby otrzymywać powiadomienia push, włącz je w tej przeglądarce.",
      messages: "Wiadomości tekstowe",
      upcomingTrainings: "Nadchodzące treningi",
      newBookingRequests: "Nowe rezerwacje",
      emailNotifications: "Powiadomienia e-mail",
      smsNotifications: "Powiadomienia SMS",
      on: "Włączone",
      off: "Wyłączone",
      saveError: "Nie udało się zapisać ustawień. Spróbuj ponownie.",
      subscribed: "Powiadomienia push są włączone w tej przeglądarce.",
      disable: "Wyłącz",
      disabling: "Wyłączanie…",
      blocked: "Zablokowałeś powiadomienia w ustawieniach przeglądarki — włącz je tam ręcznie, aby je tu otrzymywać.",
      unsupported: "Ta przeglądarka nie obsługuje powiadomień push.",
    },
  },

  location: {
    settings: {
      title: "Dostęp do lokalizacji",
      description: "Pozwól przeglądarce udostępniać Twoją lokalizację, aby pokazywać trenerów w Twojej okolicy.",
      granted: "Dostęp do lokalizacji jest włączony w tej przeglądarce.",
      prompt: "Włącz dostęp do lokalizacji w tej przeglądarce.",
      blocked: "Zablokowałeś dostęp do lokalizacji w ustawieniach przeglądarki — włącz go tam ręcznie, aby z niej korzystać.",
      unsupported: "Ta przeglądarka nie obsługuje geolokalizacji.",
      enable: "Włącz dostęp",
      enabling: "Włączanie…",
      error: "Coś poszło nie tak. Sprawdź ustawienia przeglądarki i spróbuj ponownie.",
      autoDetectLabel: "Automatyczne wykrywanie lokalizacji",
      autoDetectHint:
        "Gdy wyłączone, aplikacja nie będzie prosić o Twoją lokalizację ani z niej korzystać — wyszukiwarka pozostanie ustawiona ręcznie. Uprawnienie w przeglądarce zostaje bez zmian, to tylko przełącznik po naszej stronie.",
      on: "Włączone",
      off: "Wyłączone",
      saveError: "Nie udało się zapisać ustawień. Spróbuj ponownie.",
    },
  },

  userMenu: {
    settings: "Ustawienia",
    billing: "Rozliczenia",
  },

  billingPage: {
    title: "Rozliczenia",
    subtitle: "Odbyte treningi pogrupowane według miesięcy. Zaznacz te, które chcesz rozliczyć.",
    empty: "Nie masz jeszcze odbytych treningów do rozliczenia.",
    emptyInRange: "Brak odbytych treningów w wybranym okresie.",
    filter: {
      from: "Od",
      to: "Do",
      apply: "Pokaż",
      reset: "Bieżący i poprzedni miesiąc",
      defaultHint: "Domyślnie pokazujemy bieżący i poprzedni miesiąc.",
    },
    monthTotal: "Razem w miesiącu",
    selectMonth: "Zaznacz cały miesiąc",
    selectRow: (client: string) => `Zaznacz trening z ${client}`,
    selectedCount: (n: number) => `Zaznaczono: ${n}`,
    selectedTotal: "Suma zaznaczonych",
    generateInvoice: "Generuj fakturę",
    generateInvoiceSoon: "Generowanie faktur pojawi się wkrótce",
    columns: {
      date: "Data",
      activity: "Zajęcia",
      location: "Miejsce",
      client: "Klient",
      price: "Cena",
      payment: "Płatność",
    },
    payment: {
      paid: "Opłacony",
      unpaid: "Nieopłacony",
      refunded: "Zwrócony",
    },
  },

  settingsPage: {
    title: "Ustawienia",
    profileNav: "Dane profilu",
    notificationsNav: "Powiadomienia",
    notificationsTitle: "Powiadomienia",
    notificationsDescription: "Wybierz, o czym i w jaki sposób chcesz otrzymywać powiadomienia.",
    locationNav: "Lokalizacja",
    passwordNav: "Hasło",
    accountNav: "Konto",
    childrenNav: "Dzieci",
  },

  profileData: {
    title: "Dane profilu",
    description: "Wyświetlana nazwa, e-mail i płeć.",
    nameLabel: "Imię i nazwisko lub pseudonim",
    nameHint: "Widoczna dla innych, np. na czacie i w opiniach.",
    emailLabel: "Adres e-mail",
    emailHint: "Zmiana adresu e-mail nie jest jeszcze dostępna.",
    genderLabel: "Płeć",
    genderNone: "Nie wybrano",
    genderFemale: "Kobieta",
    genderMale: "Mężczyzna",
    genderOther: "Inne",
    genderUndisclosed: "Wolę nie podawać",
    phoneLabel: "Numer telefonu",
    phoneHint: "Niewidoczny dla innych. Używamy go tylko do powiadomień SMS o odwołanych i zmienionych treningach oraz przypomnień.",
    phoneInvalidError: "Podaj poprawny numer telefonu, np. 600 123 456.",
    saveButton: "Zapisz",
    saving: "Zapisywanie...",
    success: "Dane zostały zapisane.",
    nameRequiredError: "Podaj imię i nazwisko lub pseudonim.",
    nameTooLongError: "Nazwa może mieć maksymalnie 80 znaków.",
    genericError: "Nie udało się zapisać danych. Spróbuj ponownie.",
  },

  changePassword: {
    title: "Hasło",
    changeDescription: "Zmień hasło do konta. Pozostałe urządzenia zostaną wylogowane.",
    googleOnlyNote:
      "Logujesz się przez Google, więc zmiana hasła jest niedostępna dla tego konta.",
    currentPasswordLabel: "Obecne hasło",
    newPasswordLabel: "Nowe hasło",
    confirmPasswordLabel: "Powtórz nowe hasło",
    changeButton: "Zmień hasło",
    currentRequiredError: "Podaj obecne hasło.",
    tooShortError: "Nowe hasło musi mieć co najmniej 9 znaków.",
    tooLongError: "Hasło może mieć maksymalnie 128 znaków.",
    wrongCurrentError: "Obecne hasło jest nieprawidłowe.",
    saving: "Zapisywanie...",
    success: "Hasło zostało zapisane.",
    sameAsCurrentError: "Nowe hasło musi różnić się od obecnego.",
    genericError: "Nie udało się zapisać hasła. Spróbuj ponownie.",
  },

  accountDeletion: {
    title: "Usuń konto",
    description:
      "Usunięcie konta jest nieodwracalne. Twój profil, wiadomości i wszystkie treningi zostaną trwale usunięte.",
    deleteButton: "Usuń konto",
    retentionNote:
      "Dane rozliczeniowe rezerwacji i płatności oraz adres e-mail zachowamy wyłącznie przez okres wymagany przepisami (do 6 lat), bez danych profilu i wiadomości.",
    pauseHint: "Zamiast usuwać konto, możesz na jakiś czas zawiesić swoje zajęcia — Twój profil i dane zostaną zachowane.",
    pauseLink: "Zawieś zajęcia",
    confirmTitle: "Usunąć konto na stałe?",
    confirmMessage:
      "Ta operacja trwale usunie Twój profil, wszystkie czaty i wszystkie treningi. Nie można jej cofnąć.",
    confirmTip: "Podaj powód usunięcia konta — pomoże nam to poprawić serwis.",
    confirmButton: "Usuń konto na stałe",
    deleting: "Usuwanie...",
    reasonLabel: "Powód usunięcia konta",
    reasonPlaceholder: "Dlaczego usuwasz konto?",
    reasonRequired: "Podaj powód usunięcia konta.",
    genericError: "Nie udało się usunąć konta. Spróbuj ponownie.",
    refundsPending: "Nie możemy teraz usunąć konta: trwa zwrot płatności za odwołane zajęcia. Spróbuj ponownie za kilka godzin.",
  },

  language: {
    switchTo: (name: string) => `Przełącz na ${name}`,
    polish: "Polski",
    english: "Angielski",
  },

  footer: {
    tagline: "Znajdź trenera dopasowanego do Ciebie i trenuj tak, jak lubisz.",
    links: {
      findTrainer: "Znajdź trenera",
      howItWorks: "Jak to działa",
      forCoach: "Dla trenerów",
      contact: "Kontakt",
      help: "Pomoc",
      terms: "Regulamin",
      trainerTerms: "Regulamin trenera",
      privacy: "Polityka prywatności",
      cookies: "Cookies",
      cookieSettings: "Ustawienia cookies",
    },
    social: {
      instagram: "Instagram",
      facebook: "Facebook",
    },
    copyright: (year: number) => `© ${year} GoForCoach. Wszystkie prawa zastrzeżone.`,
  },

  legalPage: {
    comingSoon: "Ta strona pojawi się wkrótce.",
    titles: {
      contact: "Kontakt",
      help: "Pomoc",
      terms: "Regulamin",
      trainerTerms: "Regulamin dla Trenerów",
      privacy: "Polityka prywatności",
      cookies: "Cookies",
    },
  },

  contactPage: {
    intro: "Masz pytanie o rezerwację, płatność albo konto? Napisz do nas.",
    responseNote: "Odpowiemy na podany adres e-mail.",
    emailLine: "Możesz też napisać bezpośrednio na",
    supportEmail: "pomoc@goforcoach.com",
    nameLabel: "Imię",
    emailLabel: "E-mail",
    subjectLabel: "Temat",
    subjectOptions: [
      { value: "booking", label: "Rezerwacja" },
      { value: "payment", label: "Płatność lub zwrot" },
      { value: "trainer", label: "Konto trenera" },
      { value: "account", label: "Moje konto" },
      { value: "other", label: "Inne" },
    ],
    messageLabel: "Wiadomość",
    messagePlaceholder: "Opisz sprawę. Nie podawaj hasła ani pełnych danych karty.",
    send: "Wyślij wiadomość",
    sending: "Wysyłanie…",
    successTitle: "Wiadomość wysłana",
    successBody: "Dziękujemy. Odpowiemy na podany adres e-mail.",
    errors: {
      required: "Uzupełnij wszystkie pola.",
      email: "Podaj poprawny adres e-mail.",
      tooLong: "Wiadomość jest zbyt długa.",
      rateLimited: "Wysłano zbyt wiele wiadomości. Spróbuj ponownie za kilka minut.",
      captcha: "Nie udało się potwierdzić, że nie jesteś robotem. Spróbuj ponownie.",
      generic: "Nie udało się wysłać wiadomości. Spróbuj ponownie lub napisz na pomoc@goforcoach.com.",
    },
  },

  errorPages: {
    notFoundTitle: "Nie znaleźliśmy tej strony",
    notFoundBody: "Adres może być nieaktualny albo strona została przeniesiona.",
    errorTitle: "Coś poszło nie tak",
    errorBody: "Spróbuj ponownie za chwilę. Jeśli problem się powtarza, napisz do nas.",
    retry: "Spróbuj ponownie",
    backHome: "Wróć na stronę główną",
    findTrainer: "Znajdź trenera",
  },

  helpPage: {
    intro:
      "Znajdź odpowiedzi na pytania dotyczące wyboru trenera, rezerwacji, płatności i konta. Jeśli potrzebujesz pomocy, napisz na pomoc@goforcoach.com.",
    sections: [
      {
        heading: "Szukam trenera",
        items: [
          {
            q: "Czym jest GoForCoach?",
            a: "GoForCoach łączy osoby szukające treningów z trenerami różnych dyscyplin. Porównaj oferty według lokalizacji, poziomu i celu, wybierz trenera i zarezerwuj trening online.",
          },
          {
            q: "Czy korzystanie z GoForCoach jest bezpłatne?",
            a: "Założenie konta klienta i przeglądanie ofert są bezpłatne. Płacisz za wybrany trening lub pakiet, zgodnie z ceną prezentowaną podczas rezerwacji.",
          },
          {
            q: "Jak znaleźć i wybrać trenera?",
            a: "Wybierz dyscyplinę i lokalizację w wyszukiwarce. Porównaj doświadczenie, specjalizacje, ceny, dostępność i opinie klientów. Sprawdź, czy oferta odpowiada Twojemu celowi i poziomowi.",
          },
          {
            q: "Czy mogę trenować jako osoba początkująca?",
            a: "Tak. Sprawdź poziom zajęć w ofercie i wybierz trening dla początkujących. Jeśli masz wątpliwości, napisz do trenera przez czat GoForCoach.",
          },
          {
            q: "Czy znajdę treningi dla dzieci?",
            a: `Tak. Trener w ofercie wskazuje, czy zajęcia są dla dorosłych, dla dzieci i w jakim wieku. W wyszukiwarce wybierz filtr „Dla kogo” i wiek dziecka. Dzieci zapisz w ustawieniach konta — rezerwujesz z własnego konta, wybierając dziecko przy rezerwacji.`,
          },
          {
            q: "Czy mogę trenować online?",
            a: "Tak, na GoForCoach dostępne są także treningi online. Wybierz trenera oferującego taką formę zajęć i sprawdź szczegóły jego oferty.",
          },
          {
            q: "Nie znajduję trenera w swojej okolicy lub dyscyplinie. Co zrobić?",
            a: "Spróbuj zmienić lokalizację lub sprawdź treningi online. Możesz też napisać na pomoc@goforcoach.com i podać miasto oraz dyscyplinę, której szukasz.",
          },
          {
            q: "Jak skontaktować się z trenerem?",
            a: "Napisz do trenera przez czat GoForCoach. Możesz zapytać o ofertę, miejsce zajęć i ustalić szczegóły dotyczące treningu.",
          },
        ],
      },
      {
        heading: "Rezerwacje i płatności",
        items: [
          {
            q: "Jak zarezerwować trening?",
            a: "Na profilu trenera wybierz zajęcia i dostępny termin, sprawdź szczegóły rezerwacji, a następnie opłać trening przez GoForCoach.",
          },
          {
            q: "Czy do rezerwacji potrzebuję konta?",
            a: "Tak. Bezpłatne konto pozwala rezerwować treningi, kontaktować się z trenerem i sprawdzać swoje rezerwacje.",
          },
          {
            q: "Kiedy i jak płacę za trening?",
            a: "Płacisz podczas rezerwacji. Płatności obsługuje Stripe. Dostępne metody oraz pełną kwotę do zapłaty zobaczysz przed zatwierdzeniem płatności.",
          },
          {
            q: "Kto ustala cenę treningu?",
            a: "Cenę ustala trener. Sprawdź cenę, czas trwania i zakres usługi przed dokonaniem rezerwacji.",
          },
          {
            q: "Gdzie znajdę moje rezerwacje i płatności?",
            a: "Zaloguj się na swoje konto, aby sprawdzić zaplanowane treningi oraz informacje o rezerwacjach i płatnościach.",
          },
          {
            q: "Czy mogę kupić pakiet treningów lub subskrypcję?",
            a: "Tak. Dostępne są pakiety i subskrypcje odnawiane automatycznie. Sprawdź ofertę trenera oraz warunki wybranego pakietu lub subskrypcji przed zakupem.",
          },
          {
            q: "Co oznacza automatyczne odnawianie subskrypcji?",
            a: "Subskrypcja odnawia się automatycznie zgodnie z warunkami wybranej oferty. Przed zakupem sprawdź częstotliwość odnowienia, cenę i zasady zakończenia subskrypcji.",
          },
          {
            q: "Czy mogę zmienić termin treningu?",
            a: "Tak, zmianę terminu uzgodnij z trenerem przez czat GoForCoach. Napisz jak najszybciej. Nowy termin wymaga ustalenia z trenerem.",
          },
          {
            q: "Do kiedy mogę odwołać trening i co ze zwrotem?",
            a: "Trening możesz odwołać najpóźniej 24 godziny przed jego rozpoczęciem. Jak najszybciej skontaktuj się z trenerem przez czat GoForCoach. Przy odwołaniu z zachowaniem tego terminu otrzymasz pełny zwrot kwoty zapłaconej za trening.",
          },
          {
            q: "Co jeśli odwołam trening mniej niż 24 godziny przed rozpoczęciem?",
            a: "Przy odwołaniu później niż 24 godziny przed rozpoczęciem treningu płatność nie podlega zwrotowi. Możesz jednak uzgodnić z trenerem inny termin przez czat GoForCoach.",
          },
          {
            q: "Co jeśli trener odwoła trening?",
            a: "Trener powinien poinformować Cię o odwołaniu i uzgodnić z Tobą, czy wybierasz zwrot pieniędzy, czy nowy termin. Szczegóły ustalcie przez czat GoForCoach.",
          },
          {
            q: "Co jeśli trener nie pojawi się na treningu?",
            a: "Napisz do trenera przez czat GoForCoach. Jeśli nie możesz wyjaśnić sytuacji, zgłoś ją na pomoc@goforcoach.com. Podaj informacje pozwalające zidentyfikować rezerwację.",
          },
          {
            q: "Płatność się nie powiodła. Co zrobić?",
            a: "Sprawdź status rezerwacji i czy rachunek został obciążony. Jeśli pobrano pieniądze, ale nie widzisz potwierdzenia rezerwacji, napisz na pomoc@goforcoach.com przed ponowną płatnością.",
          },
          {
            q: "Czy mogę wybrać innego trenera?",
            a: "Tak, na kolejne treningi możesz wybrać innego trenera.",
          },
        ],
      },
      {
        heading: "Dla trenerów",
        items: [
          {
            q: "Jak założyć profil trenera?",
            a: "Załóż bezpłatne konto trenera i uzupełnij profil. Dodaj zdjęcie, doświadczenie, specjalizacje, ofertę, ceny, miejsca zajęć i dostępne terminy. Możesz zarejestrować się także przy użyciu konta Google.",
          },
          {
            q: "Czy konto trenera jest płatne?",
            a: "Nie. Założenie konta i profilu trenera jest bezpłatne.",
          },
          {
            q: "Jak działa prowizja?",
            a: "Prowizja wynosi 5% od zrealizowanego treningu. Im więcej treningów odbędziesz w miesiącu, tym niższa prowizja.",
          },
          {
            q: "Jak ustawić ceny i zarządzać dostępnością?",
            a: "W panelu trenera ustaw ceny swoich usług i dostępne terminy. Aktualizuj grafik, aby klienci mogli rezerwować treningi w godzinach, w których prowadzisz zajęcia.",
          },
          {
            q: "Czy mogę prowadzić zajęcia w kilku miejscach lub online?",
            a: "Tak. Możesz dodać kilka lokalizacji i oferować treningi online. Przy każdej usłudze jasno wskaż miejsce lub formę prowadzenia zajęć.",
          },
          {
            q: "Czy mogę oferować pakiety i subskrypcje?",
            a: "Tak, możesz oferować pakiety treningów oraz subskrypcje odnawiane automatycznie. Opisz zakres oferty i warunki korzystania przed jej udostępnieniem klientom.",
          },
          {
            q: "Kiedy otrzymuję pieniądze za trening?",
            a: "Pieniądze należne trenerowi są przekazywane przez Stripe po wykonanym treningu. Zazwyczaj trwa to 1–3 dni.",
          },
          {
            q: "Czy potrzebuję konta Stripe?",
            a: "Tak. Stripe obsługuje płatności i wypłaty, a konto Stripe jest niezbędne do aktywacji konta trenera i rozliczeń. Założenie konta Stripe jest bezpłatne.",
          },
          {
            q: "Co jeśli klient odwoła trening w ostatniej chwili?",
            a: "Jeżeli klient odwoła trening później niż 24 godziny przed jego rozpoczęciem, płatność nie podlega zwrotowi. Możecie jednak uzgodnić inny termin przez czat GoForCoach.",
          },
          {
            q: "Co zrobić, jeśli muszę odwołać trening?",
            a: "Jak najszybciej poinformuj klienta przez GoForCoach. Ustal z nim, czy wybiera zwrot pieniędzy, czy nowy termin treningu.",
          },
          {
            q: "Jak zwiększyć szanse na rezerwacje?",
            a: "Dodaj aktualne zdjęcie i konkretny opis doświadczenia. Wyjaśnij, komu pomagasz, podaj ceny, miejsca zajęć i dostępne terminy. Dbaj o aktualność grafiku i odpowiadaj na wiadomości klientów.",
          },
        ],
      },
      {
        heading: "Konto i pomoc",
        items: [
          {
            q: "Jak mogę się zalogować?",
            a: "Zaloguj się danymi swojego konta albo skorzystaj z logowania przez Google.",
          },
          {
            q: "Nie pamiętam hasła. Co zrobić?",
            a: "Na ekranie logowania wybierz opcję odzyskania hasła i postępuj zgodnie z instrukcjami.",
          },
          {
            q: "Jak zmienić dane lub usunąć konto?",
            a: "Dane zmienisz w ustawieniach profilu. Jeśli potrzebujesz pomocy przy usunięciu konta, napisz na pomoc@goforcoach.com.",
          },
          {
            q: "Czy mogę wystawić opinię trenerowi?",
            a: "Tak. Po odbytym treningu możesz wystawić opinię. Opisz swoje doświadczenie, aby pomóc innym wybrać trenera.",
          },
          {
            q: "Gdzie znajdę informacje o moich danych?",
            a: "Zasady przetwarzania danych i informacje o Twoich prawach znajdziesz w Polityce Prywatności GoForCoach.",
          },
          {
            q: "Jak zgłosić problem lub skontaktować się z GoForCoach?",
            a: "Napisz na pomoc@goforcoach.com. Opisz problem i podaj informacje pozwalające zidentyfikować rezerwację, jeśli jej dotyczy. Nie przesyłaj hasła ani pełnych danych karty płatniczej.",
          },
        ],
      },
    ],
    stillNeedHelp: {
      heading: "Nadal potrzebujesz pomocy",
      body: "Nie znalazłeś odpowiedzi? Napisz do nas — chętnie pomożemy. Skontaktuj się z GoForCoach: pomoc@goforcoach.com.",
    },
  },

  childProfiles: {
    title: "Dzieci",
    description: "Zapisz swoje dzieci, aby rezerwować dla nich treningi z własnego konta.",
    empty: "Nie dodano jeszcze żadnego dziecka.",
    firstNameLabel: "Imię dziecka",
    birthDateLabel: "Data urodzenia",
    add: "Dodaj dziecko",
    save: "Zapisz",
    saving: "Zapisywanie…",
    cancel: "Anuluj",
    edit: "Edytuj",
    archive: "Usuń",
    archiveConfirm: (name: string) =>
      `Usunąć ${name}? Zaplanowane treningi pozostaną w mocy, ale dziecko zniknie z listy.`,
    ageYears: (n: number) =>
      `${n} ${n === 1 ? "rok" : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? "lata" : "lat"}`,
    attendeeLabel: (childName: string, age: number, parentName: string) =>
      `${childName} (${age}) — rodzic: ${parentName}`,
    errors: {
      notSignedIn: "Zaloguj się, aby zarządzać dziećmi.",
      firstNameRequired: "Podaj imię dziecka.",
      birthDateInvalid: "Podaj poprawną datę urodzenia.",
      birthDateFuture: "Data urodzenia nie może być z przyszłości.",
      mustBeUnder18: "Dziecko musi mieć mniej niż 18 lat.",
      notFound: "Nie znaleziono dziecka.",
    },
    picker: {
      forWhom: "Dla kogo?",
      forMe: "Dla mnie",
      forChild: "Dla dziecka",
      pickChild: "Wybierz dziecko",
      saveChild: "Zapisz dziecko",
      noChildren: "Nie masz jeszcze zapisanego dziecka.",
      ageRange: (minAge: number, maxAge: number) => `wiek ${minAge}–${maxAge} lat`,
      loginToAddChild: "Zaloguj się, aby zarezerwować trening dla dziecka.",
    },
  },

  trainerTermsPage: {
    accept: "Akceptuję regulamin",
    accepting: "Zapisywanie…",
    acceptedOn: (date: string) => `Regulamin zaakceptowany ${date}`,
    loginHint: "Zaloguj się jako trener, aby zaakceptować regulamin.",
    notTrainerHint: "Regulamin można zaakceptować po utworzeniu profilu trenera.",
    backToProfile: "Wróć do profilu trenera",
    error: "Nie udało się zapisać akceptacji. Spróbuj ponownie.",
  },

  cookieConsent: {
    title: "Ustawienia plików cookie",
    description:
      "Używamy plików cookie, aby aplikacja działała poprawnie oraz — za Twoją zgodą — abyśmy mogli lepiej zrozumieć, jak z niej korzystasz i poprawiać Twoje doświadczenie.",
    acceptAll: "Akceptuj wszystkie",
    rejectNonEssential: "Odrzuć niekonieczne",
    customize: "Dostosuj",
    savePreferences: "Zapisz ustawienia",
    categories: {
      necessary: {
        name: "Niezbędne",
        description:
          "Umożliwiają zalogowanie i zapamiętują wybrany język. Nie można ich wyłączyć — bez nich aplikacja nie działa.",
      },
      analytics: {
        name: "Analiza",
        description:
          "Pomaga nam zrozumieć, jak korzystasz z aplikacji, abyśmy mogli poprawiać Twoje doświadczenie. Opcjonalne.",
      },
    },
  },

  cookiePolicy: {
    intro: "Poniżej znajdziesz pełną listę plików cookie używanych przez GoForCoach oraz do czego służą.",
    manageButton: "Zarządzaj ustawieniami cookie",
    table: {
      nameHeader: "Nazwa",
      purposeHeader: "Cel",
      durationHeader: "Czas przechowywania",
      categoryHeader: "Kategoria",
    },
    categoryLabels: {
      necessary: "Niezbędne",
      analytics: "Analiza",
    },
    rows: [
      {
        name: "better-auth.session_token i powiązane",
        purpose: "Utrzymują Cię zalogowanym.",
        duration: "7 dni",
        category: "necessary",
      },
      {
        name: "locale",
        purpose: "Zapamiętuje wybrany język aplikacji.",
        duration: "1 rok",
        category: "necessary",
      },
      {
        name: "analytics_consent",
        purpose: "Zapamiętuje Twój wybór dotyczący zgody na analitykę.",
        duration: "1 rok",
        category: "necessary",
      },
      {
        name: "cc_cookie",
        purpose: "Zapamiętuje treść okna zgody na pliki cookie, które właśnie czytasz.",
        duration: "182 dni",
        category: "necessary",
      },
      {
        name: "identyfikator analityczny",
        purpose:
          "Pomaga nam zrozumieć, jak korzystasz z aplikacji, aby ją ulepszać. Ustawiany tylko po wyrażeniu zgody.",
        duration: "do 1 roku",
        category: "analytics",
      },
    ],
  },
  groupAbsence: {
    groupLabel: "Grupa",
    makeupLabel: "Odrabianie",
    reportButton: "Zgłoś nieobecność",
    confirmTitle: "Zgłosić nieobecność?",
    confirmMessage:
      "Trener dostanie powiadomienie i wyznaczy termin odrobienia zajęć. Możesz cofnąć zgłoszenie, dopóki termin nie zostanie wyznaczony.",
    confirmYes: "Zgłoś nieobecność",
    confirmNo: "Wróć",
    reportedText: "Nieobecność zgłoszona — trener wyznaczy termin odrobienia.",
    withdrawButton: "Cofnij zgłoszenie",
    working: "Chwileczkę…",
    makeupScheduledText: (when: string) => `Odrabianie: ${when}.`,
    closedText: "Nieobecność zamknięta — termin odrobienia minął.",
    completedText: "Zajęcia odrobione.",
    notCompletedText: "Odrabianie nie odbyło się.",
    makeupSessionText: "To zajęcia odrabiane w miejsce nieobecności.",
  },
  groupAbsences: {
    summary: (open: number) => (open > 0 ? `Nieobecności (${open} do obsłużenia)` : "Nieobecności"),
    empty: "Brak nieobecności.",
    missed: (when: string) => `Nieobecność: ${when}`,
    deadline: (date: string) => `odrobić do ${date}`,
    statusReported: "Zgłoszona",
    statusMakeupScheduled: "Odrabianie zaplanowane",
    statusCompleted: "Odrobiona",
    statusNotCompleted: "Nieodrobiona",
    statusClosed: "Zamknięta",
    makeupSlot: (when: string) => `Odrabianie: ${when}`,
    startLabel: "Początek odrabiania",
    assignButton: "Wyznacz termin",
    working: "Chwileczkę…",
    cancelButton: "Odwołaj odrabianie",
    cancelTitle: "Odwołać odrabianie?",
    cancelMessage: "Uczestnik dostanie powiadomienie, a nieobecność wróci na listę do wyznaczenia terminu.",
    cancelYes: "Odwołaj",
    cancelNo: "Wróć",
    decideCompleted: "Odbyło się",
    decideNotCompleted: "Nie odbyło się",
    problemInvalid: "Wpisz poprawny początek i koniec.",
    problemPast: "Termin musi być w przyszłości.",
    problemOrder: "Koniec musi być po początku.",
    problemDuration: "Odrabianie może trwać od 15 minut do 8 godzin.",
    problemDeadline: "Termin wypada po dniu, do którego można odrobić zajęcia.",
    genericError: "Coś poszło nie tak. Spróbuj ponownie.",
    lockedGroup: "Grupa",
    lockedMakeup: "Odrabianie",
  },
  groups: {
    navGroups: "Grupy",
    views: {
      mine: "Moje grupy",
      schedule: "Harmonogram i opłata",
      members: "Uczestnicy",
      absences: "Nieobecności i odrabianie",
      refunds: "Rozliczenia",
    },
    calendar: {
      group: "Grupa",
      month: "Miesiąc",
      monthHint: "Kliknij dzień, aby go zaznaczyć, i jeszcze raz, aby dodać, przenieść lub usunąć zajęcia tego dnia.",
      legendClasses: "Zajęcia grupy",
      noMonthYet: "Ten miesiąc nie jest jeszcze przygotowany.",
      locked: "Ten miesiąc jest zatwierdzony. Jego terminów nie zmienisz tutaj; zajęcie możesz odwołać: wybierz dzień.",
      pastBlocked: "Nie można dodać zajęć w przeszłości.",
      outsideMonth: "Zajęcia muszą być w tym samym miesiącu.",
      clash: (what: string) => `Ta godzina jest zajęta: ${what}.`,
      added: "Dodano zajęcia.",
      exists: "Zajęcia o tej porze już są tego dnia.",
      addedMany: (what, skipped) => `Dodano: ${what}.${skipped > 0 ? ` Pominięto ${skipped} (przeszłość, zajęty termin albo już jest).` : ""}`,
      addNone: (skipped) => `Nic nie dodano: wszystkie terminy (${skipped}) są pominięte (przeszłość, zajęty termin albo już są).`,
      moved: "Przeniesiono zajęcia.",
      deleted: "Usunięto zajęcia.",
      pickDay: "Wybierz dzień w kalendarzu.",
      dayTitle: (label) => label,
      addTitle: "Dodaj zajęcia",
      time: "Godzina",
      repeat: (weekday) => `Powtarzaj w każdym tygodniu tego miesiąca (${weekday})`,
      repeatHint: "Zajęcia zostaną dodane w każdym takim dniu tygodnia do końca miesiąca.",
      add: "Dodaj",
      dayClasses: "Zajęcia grupy tego dnia",
      dayEmpty: "Brak zajęć grupy tego dnia.",
      moveTo: "Przenieś na",
      move: "Przenieś",
      remove: "Usuń",
      close: "Zamknij",
      kind: { session: "Trening", block: "Blokada", draft: "Zajęcia innej grupy (szkic)" },
      noGroups: "Najpierw utwórz grupę w „Moje grupy”.",
    },
    viewEmpty: "Najpierw utwórz grupę w „Moje grupy”.",
    goToMine: "Przejdź do „Moje grupy”",
    noPeriods: "Brak przygotowanych miesięcy. Przygotuj miesiąc w „Moje grupy”.",
    noRefunds: "Brak zwrotów dla uczestników.",
    title: "Stałe grupy",
    subtitle: "Zajęcia cykliczne z miesięcznym rozliczeniem uczestników.",
    emptyTitle: "Nie masz jeszcze żadnej grupy",
    emptyBody: "Utwórz grupę, udostępnij uczestnikom link i rozliczaj zajęcia co miesiąc.",
    newGroup: "Nowa grupa",
    createTitle: "Utwórz grupę",
    name: "Nazwa grupy",
    namePlaceholder: "np. Tenis wtorki i czwartki",
    sport: "Dyscyplina",
    location: "Miejsce",
    noLocation: "Bez wskazanego miejsca",
    capacity: "Liczba miejsc",
    durationMinutes: "Czas trwania (min)",
    paymentDueDays: "Termin płatności (dni)",
    paymentDueDaysHint: "Liczba dni na opłacenie od zatwierdzenia miesiąca.",
    rules: "Zasady grupy",
    rulesPlaceholder: "Np. odwołania, sprzęt, zasady płatności",
    visibility: "Widoczność",
    visibilityLink: "Tylko z linkiem",
    visibilityPublic: "Publiczna",
    visibilityLinkHint: "Grupa jest widoczna tylko dla osób, które dostaną od Ciebie link.",
    visibilityPublicHint: "Grupa jest widoczna w Twoim profilu dla wszystkich.",
    createSportPlaceholder: "Wybierz dyscyplinę",
    noSportHint: "Żeby utworzyć grupę, dodaj najpierw trening w zakładce Oferta — dyscyplinę wybierzesz spośród swoich treningów.",
    goToOffer: "Przejdź do oferty",
    cancelCreate: "Anuluj",
    edit: "Edytuj",
    editTitle: "Edytuj grupę",
    options: "Opcje grupy",
    deleteGroup: "Usuń grupę",
    deleteGroupBlocked: "Grupy z uczestnikami nie można usunąć.",
    deleteGroupTitle: "Usunąć grupę?",
    deleteGroupMessage: (name: string) => `Grupa „${name}” zostanie usunięta razem z całym harmonogramem i ustawieniami. Tej operacji nie można cofnąć.`,
    deleteGroupTip: "Zaplanowane treningi zostaną na Twoim kalendarzu jako zwykłe treningi.",
    deleting: "Usuwanie…",
    editHint: "Zmiany dotyczą kolejnych miesięcy. Zatwierdzone miesiące, płatności i link dla uczestników zostają bez zmian.",
    saveChanges: "Zapisz zmiany",
    err: {
      nameRequired: "Podaj nazwę grupy.",
      nameTooLong: (max) => `Nazwa może mieć maksymalnie ${max} znaków.`,
      sportRequired: "Wybierz dyscyplinę z listy.",
      numberRequired: "Wpisz liczbę.",
      numberNotInteger: "Wpisz liczbę całkowitą, bez przecinka i liter.",
      numberOutOfRange: (min, max) => `Wpisz liczbę od ${min} do ${max}.`,
      rulesTooLong: (max) => `Zasady mogą mieć maksymalnie ${max} znaków.`,
    },
    submit: "Utwórz grupę",
    submitting: "Tworzenie…",
    created: "Grupa została utworzona.",
    joinLink: "Link dla uczestników",
    copyLink: "Kopiuj link",
    linkCopied: "Skopiowano",
    copyFailed: "Nie udało się skopiować. Skopiuj link ręcznie.",
    schedule: (days, time, minutes) => `${days}, ${time} (${minutes} min)`,
    scheduleByMonth: (minutes) => `Terminy ustalane co miesiąc (${minutes} min)`,
    sessionsCount: (n) =>
      `${n} ${n === 1 ? "trening" : isPaucal(n) ? "treningi" : "treningów"}`,
    cancelSessionButton: "Odwołaj trening",
    cancelShort: "Odwołaj",
    cancelSessionTitle: "Odwołać ten trening?",
    cancelSessionMessage: (when, bookings, refund) =>
      bookings === 0
        ? `Trening ${when} zostanie odwołany. Nikt się na niego nie zapisał. Tej operacji nie można cofnąć.`
        : `Trening ${when} zostanie odwołany dla ${bookings} ${bookings === 1 ? "uczestnika" : "uczestników"}.${
            refund
              ? ` Zwrócimy im łącznie ${refund}.`
              : " Za ten trening nikt jeszcze nie zapłacił, więc nic nie zwracamy."
          } Uczestnicy dostaną powiadomienie. Tej operacji nie można cofnąć.`,
    cancelSessionYes: "Odwołaj trening",
    cancelSessionNo: "Wróć",
    cancelSessionDone: (refunds, failed) =>
      failed > 0
        ? `Trening odwołany. Zwroty: ${refunds}, w tym nieudane: ${failed} — spróbuj ponownie w zakładce Rozliczenia.`
        : refunds > 0
          ? `Trening odwołany. Zlecone zwroty: ${refunds}.`
          : "Trening odwołany.",
    cancelSessionWorking: "Odwoływanie…",
    cancelSessionError: "Nie udało się odwołać treningu. Spróbuj ponownie.",
    refundsHeading: (n, unfinished) => (unfinished > 0 ? `Zwroty (${n}, do obsłużenia: ${unfinished})` : `Zwroty (${n})`),
    refundStatusPending: "W toku",
    refundStatusSucceeded: "Zwrócono",
    refundStatusFailed: "Nieudany",
    refundStatusExhausted: "Wymaga kontaktu z obsługą",
    refundRowLine: (member, when) => `${member} · ${when}`,
    refundFailedHint:
      "Zwrot się nie powiódł. System spróbuje ponownie, możesz też ponowić ręcznie. Najczęstsza przyczyna: za mało środków na koncie Stripe.",
    refundExhaustedHint: "Zwrot nie udał się po wielu próbach. Napisz do obsługi.",
    refundRetry: "Ponów zwrot",
    refundRetrying: "Ponawianie…",
    refundRetryDone: "Zlecono ponowienie zwrotu.",
    refundRetryError: "Nie udało się ponowić zwrotu. Spróbuj ponownie.",
    sessionCancelledChip: "Odwołany",
    seatsLeft: (n, capacity) => `Wolne miejsca: ${n} z ${capacity}`,
    noApprovedPeriod: "Brak zatwierdzonego miesiąca",
    periodDraft: "Wersja robocza",
    periodApproved: "Zatwierdzony",
    periodCancelled: "Anulowany",
    prepareMonthFor: (month) => `Przygotuj: ${month}`,
    preparing: "Przygotowywanie…",
    fee: "Opłata miesięczna (zł)",
    feeHint: "Kwota na osobę za cały miesiąc, rozłożona na treningi.",
    feeOutOfRange: (min, max) => `Kwota musi mieścić się między ${min} a ${max}.`,
    feeInvalid: "Podaj poprawną kwotę, np. 500 lub 500,50.",
    termsTitle: (month) => `Terminy · ${month}`,
    termsHint: "Kliknij trening w szkicu, by go wyłączyć; kliknij brakujący dzień, by dodać termin.",
    noSession: "Brak zajęć",
    dayOn: (label) => `Wyłącz termin: ${label}`,
    dayOff: (label) => `Dodaj termin: ${label}`,
    feeTitle: "Opłata od osoby",
    summaryMonthly: "Opłata miesięczna",
    summaryPerSession: "Średnia wartość treningu",
    summaryStatus: "Status",
    approveNote: "Zatwierdzonego miesiąca nie można już edytować. Uczestnicy zostaną obciążeni opłatą.",
    pastDatesBlock: (dates) => `Terminy z przeszłości nie mogą zostać zatwierdzone: ${dates}.`,
    removePastDates: "Usuń minione terminy",
    draftSummaryRounding: "Różnica groszy przypada na pierwsze treningi.",
    save: "Zapisz",
    saving: "Zapisywanie…",
    saved: "Zapisano",
    approve: "Zatwierdź miesiąc",
    approving: "Zatwierdzanie…",
    approveConfirm: (month, sessions, fee, members) =>
      `Zatwierdzić ${month}? Treningów: ${sessions}, opłata na osobę: ${fee}. Obciążonych zostanie uczestników: ${members}. Uczestnicy zostaną obciążeni, a terminy trafią do kalendarza.`,
    approveConfirmYes: "Zatwierdź",
    approveConfirmNo: "Anuluj",
    errorGeneric: "Nie udało się wykonać operacji. Spróbuj ponownie.",
    members: "Uczestnicy",
    noMembers: "Nikt jeszcze nie dołączył.",
    memberActive: "Aktywny",
    memberPending: "Oczekujacy",
    memberEnded: "Zakończony",
    endMember: "Usuń z grupy",
    endMemberConfirm: (name) => `Zakończyć udział: ${name}?`,
    endMemberConfirmYes: "Zakończ",
    endMemberError: "Nie udało się zakończyć udziału. Spróbuj ponownie.",
    confirmCancel: "Anuluj",
    charges: "Należności uczestników",
    noCharges: "Brak naliczeń.",
    chargePending: "Do zapłaty",
    chargePaid: "Opłacone",
    chargeExpired: "Termin upłynął",
    chargeRefunded: "Zwrócone",
    dueDate: (date) => `Termin: ${date}`,
    extendDays: (n) => `Przedłuż o ${n} ${n === 1 ? "dzień" : "dni"}`,
    extending: "Przedłużanie…",
    extendChargeError: "Nie udało się przedłużyć terminu. Spróbuj ponownie.",
    chargeAmountLabel: "Kwota",
    extendExpiredNote: "Przedłużenie przeterminowanego rozliczenia wymaga wolnego miejsca w grupie.",
    joinSubtitle: "Dołącz do stałej grupy treningowej i opłać miesiąc w jednym kroku.",
    joinTrainerBy: (trainer) => `Trener: ${trainer}`,
    joinRulesHeading: "Zasady grupy",
    joinDatesHeading: "Najbliższe terminy",
    joinPriceLine: (sessions, amount, month) => `${sessions === 1 ? "Został" : isPaucal(sessions) ? "Zostały" : "Zostało"} ${sessions} ${sessions === 1 ? "trening" : isPaucal(sessions) ? "treningi" : "treningów"} ${inMonth(month)} — ${amount}`,
    joinCta: "Dołącz do grupy",
    joinChargeAmount: (amount) => `Kwota za ten miesiąc: ${amount}`,
    payLaterNote: (amount, days) =>
      days === 0
        ? `Do zapłaty ${amount} — zapłacisz w Moich treningach.`
        : `Do zapłaty ${amount} — masz na to ${days} ${days === 1 ? "dzień" : "dni"} od dołączenia, zapłacisz w Moich treningach.`,
    payCta: (amount) => `Zapłać ${amount}`,
    joining: "Przetwarzanie…",
    joinNoOfferNote: "Trener jeszcze nie otworzył zapisów na kolejny miesiąc. Możesz już dołączyć do grupy — link do płatności dostaniesz, gdy miesiąc będzie gotowy.",
    joinSignInIntro: "Załóż konto lub zaloguj się, aby dołączyć do grupy.",
    joinSignInCta: "Zaloguj się, aby dołączyć",
    joinOwnGroup: "To Twoja grupa — uczestnicy dołączają przez ten link. Zarządzasz nią w panelu trenera.",
    joinManageGroup: "Przejdź do panelu grupy",
    joinVerifyEmail: "Potwierdź swój adres e-mail, aby dołączyć do grupy i zapłacić. Sprawdź skrzynkę — wysłaliśmy Ci link.",
    joinFull: "Ta grupa nie ma już wolnych miejsc.",
    joinWaiting: "Jesteś w grupie. Trener nie otworzył jeszcze kolejnego miesiąca — dostaniesz powiadomienie z linkiem do płatności.",
    joinPaid: "Masz opłacony ten miesiąc. Treningi znajdziesz w „Moje treningi”.",
    joinGoToPanel: "Moje treningi",
    joinChargeExpired: "Termin płatności za ten miesiąc minął. Skontaktuj się z trenerem — może przedłużyć termin.",
    joinEnded: "Twój udział w tej grupie został zakończony.",
    joinRejoinCta: "Dołącz ponownie",
    joinChargeRefunded: "Płatność za ten miesiąc została zwrócona. Zapłacisz za kolejny miesiąc, gdy trener go otworzy.",
    joinDeadChargeNote: "Poprzednie rozliczenie wygasło. Dołącz ponownie i poproś trenera o przedłużenie terminu płatności.",
    serviceFeeLine: (amount) => `w tym opłata serwisowa: ${amount}`,
    dueSectionTitle: "Do opłacenia",
    dueRow: (group, month) => `${group} — ${month}`,
    dueUntil: (date) => `do ${date}`,
    dueSessions: (n) => `${n} ${n === 1 ? "trening" : isPaucal(n) ? "treningi" : "treningów"}`,
    duePartial: (n, total) => `Pozostało ${n} z ${total}`,
    payCtaFor: (amount, label) => `Zapłać ${amount} za ${label}`,
    dueExpiredNote: "Termin minął.",
    paymentProcessing: "Dziękujemy! Przetwarzamy płatność — treningi pojawią się tu za chwilę.",
    paymentRefresh: "Odśwież stronę",
    paymentCancelled: "Płatność została anulowana. Możesz opłacić miesiąc w dowolnej chwili przed terminem.",
    paying: "Przekierowanie do płatności…",
  },
  clientGroups: {
    navGroups: "Moje grupy",
    empty: "Nie należysz jeszcze do żadnej grupy. Dołącz z linku od trenera.",
    memberInvited: "Zaproszony",
    noCharge: "Brak należności",
    noMonths: "Trener nie zatwierdził jeszcze żadnego miesiąca.",
    trainerLabel: "Trener",
    participant: (name) => `Uczestnik: ${name}`,
    absencesTitle: "Nieobecności",
    notIncluded: "Poza Twoim rozliczeniem",
    joinedTitle: "Dołączono do grupy",
    joinedMessage: (group, trainer) => `Zostałeś dodany do grupy „${group}” trenera ${trainer}.`,
    joinedPayNow: (amount, due) => `Do zapłaty: ${amount}, ${due}. Opłacisz to poniżej, w zakładce grupy.`,
    joinedPaid: "Bieżący miesiąc jest już opłacony.",
    joinedNoCharge: "Płatność pojawi się, gdy trener zatwierdzi kolejny miesiąc.",
    joinedOk: "Zobacz grupę",
  },
  holidays: {
    notSignedIn: "Nie jesteś zalogowany.",
    needsTrainerProfile: "Najpierw utwórz profil trenera.",
    invalidDate: "Nieprawidłowa data.",
    notHoliday: "Ten dzień nie jest dniem wolnym od pracy.",
    pastDate: "Ten dzień już minął.",
    tooFarAhead: "Ten dzień jest zbyt odległy.",
    legend: "Święto",
    dayOff: (name) => `${name} — dzień wolny od pracy`,
    dayOffOpened: (name) => `${name} — dzień wolny od pracy, otwarte na rezerwacje`,
    weekOpenNote: (name) => `${name} · otwarte na rezerwacje`,
    openDay: "Otwórz ten dzień na rezerwacje",
    closeDay: "Zamknij ten dzień (święto)",
    toggleFailed: "Nie udało się zmienić ustawienia tego dnia. Spróbuj ponownie.",
    bookingDayOff: (name) => `Dzień wolny od pracy: ${name}. Trener nie przyjmuje rezerwacji.`,
  },
  mockPay: {
    metaTitle: "Płatność testowa",
    banner: "Płatność testowa — nie jest prawdziwa",
    bannerNote: "Środowisko deweloperskie: żadne pieniądze nie zostaną pobrane.",
    heading: "Podsumowanie płatności",
    total: "Razem",
    platformFee: (amount) => `W tym prowizja platformy: ${amount}`,
    payButton: "Zapłać",
    backButton: "Wróć",
    paidState: "Ta płatność została już opłacona.",
    expiredState: "Ta płatność wygasła.",
    backLink: "Wróć do serwisu",
  },
};
